import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Activity,
  Building2,
  AlertTriangle,
  ArrowDownRight,
  ArrowUpRight,
  CalendarDays,
  Check,
  CheckCircle2,
  ClipboardList,
  Eye,
  History,
  Loader2,
  LogIn,
  LogOut,
  MapPinned,
  MapPinOff,
  Percent,
  Plus,
  Sparkles,
  Target,
  Trash2,
  UserRoundPen,
  Users,
  XCircle,
} from "lucide-react";
import "bootstrap-icons/font/bootstrap-icons.css";
import PhoneInput, { isPossiblePhoneNumber } from "react-phone-number-input";
import "react-phone-number-input/style.css";

import { fireTaskCompletedConfetti } from "../utils/confetti";
import { formatMoneyBRL, parseMoneyBRL } from "../utils/money";
import NumberFlowValue from "../components/ui/NumberFlowValue";
import { SwalWithTheme } from "../utils/sweetalert";
import { useAppAlert } from "../context/alert-context";
import FiltersSheet from "../components/FiltersSheet";
import {
  DASHBOARD_REFRESH_REQUEST_EVENT,
  clearDashboardRefreshState,
  formatRefreshCountdown,
  publishDashboardRefreshState,
} from "../services/dashboardRefreshService";
import usePageFilters from "../hooks/usePageFilters";
import useBreadcrumbEntity from "../hooks/useBreadcrumbEntity";
import {
  listarContatosPorParceiro,
  salvarContato,
  atualizarContato,
  removerContato,
} from "../services/contatoService";
import {
  salvarTelefone,
  atualizarTelefone,
  removerTelefone,
} from "../services/telefoneService";
import {
  listarTarefasPaginado,
  buscarTarefa,
  criarTarefa as criarTarefaService,
  atualizarTarefa as atualizarTarefaService,
  removerTarefa as removerTarefaService,
  countTarefasPorUsuarioTipoStatus,
  countTarefasPorUsuarioStatus,
  checkinTarefa,
  checkoutTarefa,
  fallbackCheckinTarefa,
  fallbackCheckoutTarefa,
  evidenciarTarefa,
  concluirTarefaComPayload,
  listarChecksTarefa,
  listarHistoricoTarefa,
} from "../services/tarefaService";
import httpClient from "../services/httpClient";
import { listarTiposDeTarefa } from "../services/tipoDeTarefaService";
import { listarStatusAtividades } from "../services/statusAtividadeService";
import { listarMotivosTarefa } from "../services/motivoTarefaService";
import { getUsuarioLogado } from "../services/LoginService";
import { buscarEnderecoPorCep } from "../services/cepService";
import { buscarVidaVendedorResumo, listarBancosAtivos, listarConveniosAtivos } from "../services/joinService";
import { listarConcorrentes } from "../services/concorrenteService";
import { buscarParceiro, atualizarParceiro, listarHistoricoParceiro as listarHistoricoParceiroService } from "../services/parceiroService";
import {
  APP_NOTIFICATION_CREATED_EVENT,
  getNotificationPartnerIds,
  getNotificationTaskId,
  isApprovalNotification,
} from "../services/notificationService";
import { formatCpfCnpj, getCpfCnpjLabel, inferPhoneType } from "../utils/inputMasks";
import { getCurrentMonthRange } from "../utils/dateFilters";
import { buildTaskTitleFromNotes } from "../utils/taskText";
import {
  buildJoinPeriodoComparativo,
  calculateChange,
  formatPeriodoDescricao,
  sanitizeJoinPeriodo,
} from "../utils/joinVidaAgente";
import { buildTaskGeoPartner, parseGeoErrorInfo } from "../utils/taskGeoCheck";
import { resolveTaskTypeIconDefinition } from "../utils/taskTypeIcons";
import {
  TASK_FIELD_CODES,
  getStepPolicy,
  hasVisibleField,
  isFieldRequired,
  isFieldVisible,
  isStepEnabled,
  mergeFieldModes,
  requiresCheckin,
  requiresCheckout,
  stepRequiresApproval,
  stepRequiresImage,
} from "../utils/taskRules";
import { toneClassMap } from "../utils/themeContrast";
import { kpiAccentByLabel, kpiComparisonToneClass, kpiGradientStyle, kpiInkClasses } from "../utils/kpiCard";
import ParceiroIdentidade, { PARCEIRO_STATUS_LABEL, nomeExibicaoParceiro } from "../components/parceiro/ParceiroIdentidade";
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Button,
  Card,
  DataTable,
  DatePickerField,
  DateRangeField,
  FormField,
  Modal,
  MultiSelectField,
  PageLoader,
  SearchBar,
  StatusBadge,
  Tabs,
  Tooltip,
  getAvatarInitials,
  tabPanelProps,
} from "../components/ui";
import TaskGeoCheckDrawer from "../components/tasks/TaskGeoCheckDrawer";

const TASK_FILTER_STORAGE_PREFIX = "parceiro-detalhes:tarefas-filtros:";
const CONCLUIR_DRAFT_STORAGE_PREFIX = "parceiro-detalhes:concluir-draft:";

const calcProgress = (checks = []) => {
  const total = checks.length || 1;
  const done = checks.filter(Boolean).length;
  return Math.round((done / total) * 100);
};

function getConcluirDraftStorageKey(taskId) {
  return `${CONCLUIR_DRAFT_STORAGE_PREFIX}${taskId}`;
}

function loadConcluirDraft(taskId) {
  if (!taskId || typeof window === "undefined") return null;
  try {
    const raw = window.sessionStorage.getItem(getConcluirDraftStorageKey(taskId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return {
      relatorio: String(parsed?.relatorio || ""),
      produto: String(parsed?.produto || ""),
      comissaoParceiroAjustada:
        typeof parsed?.comissaoParceiroAjustada === "boolean" ? parsed.comissaoParceiroAjustada : null,
      loginParceiroSolicitado:
        typeof parsed?.loginParceiroSolicitado === "boolean" ? parsed.loginParceiroSolicitado : null,
      bancosConvenios: Array.isArray(parsed?.bancosConvenios)
        ? parsed.bancosConvenios.map((item) => ({
            ...createBancoLinha(),
            ...item,
            bancoId: String(item?.bancoId || ""),
            convenioId: String(item?.convenioId || ""),
            valorProduzido: String(item?.valorProduzido || ""),
            concorrenteId: String(item?.concorrenteId || ""),
            concorrenteOutro: String(item?.concorrenteOutro || ""),
          }))
        : null,
      fallbackMotivo: String(parsed?.fallbackMotivo || ""),
      evidenciaObservacao: String(parsed?.evidenciaObservacao || ""),
    };
  } catch {
    return null;
  }
}

function clearConcluirDraft(taskId) {
  if (!taskId || typeof window === "undefined") return;
  window.sessionStorage.removeItem(getConcluirDraftStorageKey(taskId));
}

function loadTaskFiltersFromSession(parceiroId) {
  const defaultRange = getCurrentMonthRange();
  const fallback = {
    searchTerm: "",
    filterStatusIds: [],
    filterTipoIds: [],
    filtroDataInicio: defaultRange.from,
    filtroDataFim: defaultRange.to,
  };

  if (typeof window === "undefined") return fallback;
  try {
    const raw = window.sessionStorage.getItem(`${TASK_FILTER_STORAGE_PREFIX}${parceiroId}`);
    if (!raw) return fallback;
    const parsed = JSON.parse(raw);
    return {
      ...fallback,
      searchTerm: String(parsed?.searchTerm || ""),
      filterStatusIds: Array.isArray(parsed?.filterStatusIds) ? parsed.filterStatusIds.map(String) : [],
      filterTipoIds: Array.isArray(parsed?.filterTipoIds) ? parsed.filterTipoIds.map(String) : [],
      filtroDataInicio: parsed?.filtroDataInicio || fallback.filtroDataInicio,
      filtroDataFim: parsed?.filtroDataFim || fallback.filtroDataFim,
    };
  } catch {
    return fallback;
  }
}

function formatCurrency(value) {
  const numeric = Number(value || 0);
  return numeric.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

function formatPercent(value) {
  const numeric = Number(value || 0);
  return `${numeric.toFixed(2)}%`;
}

function formatCount(value) {
  return Number(value || 0).toLocaleString("pt-BR");
}

function buildComparisonTitle(currentLabel, previousLabel) {
  if (!previousLabel) return undefined;
  return `Atual: ${currentLabel}\nAnterior comparado: ${previousLabel}`;
}

function JoinComparisonPill({ change, title }) {
  if (!change) return null;

  const isUp = change.direction === "up";
  const isDown = change.direction === "down";
  const noBase = change.percent == null;
  const Icon = isDown ? ArrowDownRight : ArrowUpRight;
  const toneClass = kpiComparisonToneClass(change);

  const percentLabel =
    change.percent == null
      ? "sem base"
      : `${Number(change.percent).toLocaleString("pt-BR", {
          minimumFractionDigits: 0,
          maximumFractionDigits: 1,
        })}%`;

  return (
    <Tooltip content={title}>
      <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold shadow-sm [text-shadow:none] ${toneClass}`}>
        {!noBase && (isUp || isDown) ? <Icon className="h-3.5 w-3.5" /> : null}
        {percentLabel}
      </span>
    </Tooltip>
  );
}

function toDateTimeLocal(value) {
  if (!value) return "";
  const date = new Date(value);
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 16);
}

function normalizeDateTime(value) {
  if (!value) return null;
  const normalized = value.length === 16 ? `${value}:00` : value;
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/.exec(normalized);
  if (match) {
    const [, year, month, day, hour, minute, second] = match;
    const localDate = new Date(
      Number(year),
      Number(month) - 1,
      Number(day),
      Number(hour),
      Number(minute),
      Number(second || 0),
      0
    );
    if (!Number.isNaN(localDate.getTime())) {
      return localDate.toISOString();
    }
  }

  const parsed = new Date(normalized);
  return Number.isNaN(parsed.getTime()) ? null : parsed.toISOString();
}

const TASK_SCHEDULE_WINDOW_DAYS = 90;
const TASK_SCHEDULE_PAST_WINDOW_DAYS = 14;

function buildTaskScheduleBounds(now = new Date()) {
  const minDate = new Date(now);
  minDate.setDate(minDate.getDate() - TASK_SCHEDULE_PAST_WINDOW_DAYS);
  minDate.setHours(0, 0, 0, 0);
  const maxDate = new Date(now);
  maxDate.setDate(maxDate.getDate() + TASK_SCHEDULE_WINDOW_DAYS);
  return {
    min: toDateTimeLocal(minDate),
    max: toDateTimeLocal(maxDate),
  };
}

function isTaskScheduleOutsideWindow(value, bounds) {
  if (!value || !bounds?.min || !bounds?.max) return false;
  const selectedDate = new Date(normalizeDateTime(value));
  const minDate = new Date(normalizeDateTime(bounds.min));
  const maxDate = new Date(normalizeDateTime(bounds.max));

  if ([selectedDate, minDate, maxDate].some((date) => Number.isNaN(date.getTime()))) {
    return false;
  }

  return selectedDate < minDate || selectedDate > maxDate;
}

function parseJsonSafe(value, fallback) {
  if (!value || typeof value !== "string") return fallback;
  try {
    const parsed = JSON.parse(value);
    return parsed ?? fallback;
  } catch {
    return fallback;
  }
}

function createBancoLinha() {
  return {
    bancoId: "",
    convenioId: "",
    valorProduzido: "",
    concorrenteId: "",
    concorrenteOutro: "",
  };
}

function mapDadosFinanceirosParaLinhas(dadosFinanceiros = []) {
  if (!Array.isArray(dadosFinanceiros) || dadosFinanceiros.length === 0) {
    return [createBancoLinha()];
  }

  const linhas = [];
  dadosFinanceiros.forEach((item) => {
    const bancoId = String(item?.bancoId || item?.banco?.id || "");
    const concorrenteId = String(item?.concorrenteId || item?.concorrente?.id || "");
    const concorrenteOutro = String(item?.concorrenteOutro || "");

    if (item?.convenioId || item?.convenioNome || item?.valorProduzido != null) {
      linhas.push({
        ...createBancoLinha(),
        bancoId,
        convenioId: String(item.convenioId || ""),
        valorProduzido: formatMoneyBRL(item.valorProduzido),
        concorrenteId,
        concorrenteOutro,
      });
      return;
    }

    const conveniosArray = Array.isArray(item?.convenios) ? item.convenios : [];
    if (!conveniosArray.length) {
      linhas.push({
        ...createBancoLinha(),
        bancoId,
        valorProduzido: formatMoneyBRL(item.valorProduzido),
        concorrenteId,
        concorrenteOutro,
      });
      return;
    }

    conveniosArray.forEach((convenio) => {
      linhas.push({
        ...createBancoLinha(),
        bancoId,
        convenioId: String(convenio?.convenioId || convenio?.ConvenioId || convenio?.id || ""),
        valorProduzido: formatMoneyBRL(item.valorProduzido),
        concorrenteId,
        concorrenteOutro,
      });
    });
  });

  return linhas.length ? linhas : [createBancoLinha()];
}

function normalizeKey(text) {
  return String(text || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase();
}

const MOTIVOS_VISIBLE_BUDGET = 2;
// Mesmo intervalo do dashboard, com a contagem na barra superior. Recarga silenciosa: sem carregando
// de tela cheia nem alerta de erro.
const AUTO_REFRESH_SECONDS = 30;

function mapParceiroParaForm(data) {
  return {
    nome: data?.nome || "",
    documento: formatCpfCnpj(data?.documento || ""),
    cep: data?.cep || "",
    endereco: data?.endereco || "",
    bairro: data?.bairro || "",
    cidade: data?.cidade || "",
    uf: data?.uf || "",
    nu_endereco: data?.nu_endereco || "",
    status: data?.status || "ativo",
    idJoin: data?.idJoin || "",
  };
}

// "Rua X, 123 — Bairro — Cidade/UF · CEP 00000-000", pulando o que estiver vazio.
function formatEnderecoLinha({ endereco, nu_endereco, bairro, cidade, uf, cep }) {
  const rua = [endereco, nu_endereco].filter(Boolean).join(", ");
  const local = [cidade, uf].filter(Boolean).join("/");
  const linha = [rua, bairro, local].filter(Boolean).join(" — ");
  return [linha, cep ? `CEP ${cep}` : ""].filter(Boolean).join(" · ");
}

function InfoItem({ label, children, className = "" }) {
  return (
    <div className={`min-w-0 ${className}`}>
      <dt className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{label}</dt>
      <dd className="mt-1 break-words text-sm font-medium text-foreground">{children || "—"}</dd>
    </div>
  );
}
const HISTORY_PAGE_SIZE = 50;
const EMPTY_HISTORY_PAGE = {
  content: [],
  page: 0,
  size: HISTORY_PAGE_SIZE,
  totalElements: 0,
  totalPages: 0,
  last: true,
  sort: "",
};
const HISTORY_CATEGORY_CONFIG = [
  { key: "parceiro", label: "Parceiro", icon: UserRoundPen },
  { key: "tarefa", label: "Tarefa", icon: CalendarDays },
  { key: "vinculo", label: "Vinculo", icon: Target },
  { key: "execucao", label: "Execucao", icon: CheckCircle2 },
  { key: "aprovacao", label: "Aprovação", icon: Eye },
  { key: "alerta", label: "Alerta", icon: AlertTriangle },
];

function formatHistoryDateTime(value) {
  if (!value) return "-";
  let normalized = value;
  if (typeof value === "string" && value.includes("T") && !value.endsWith("Z") && !/[+-]\d{2}:?\d{2}$/.test(value)) {
    normalized = value + "Z";
  }
  const date = new Date(normalized);
  if (Number.isNaN(date.getTime())) return "-";
  return new Intl.DateTimeFormat("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

function getHistoryToneClasses(category) {
  switch (category) {
    case "parceiro":
      return "border-info/25 bg-info/10 text-info";
    case "tarefa":
      return "border-primary/25 bg-primary/10 text-primary";
    case "vinculo":
      return "border-gold/25 bg-gold/10 text-gold";
    case "execucao":
      return "border-success/25 bg-success/10 text-success";
    case "aprovacao":
      return "border-info/25 bg-info/10 text-info";
    case "alerta":
      return "border-destructive/25 bg-destructive/10 text-destructive";
    default:
      return "border-border bg-muted/25 text-muted-foreground";
  }
}

function parseMotivos(value) {
  if (!value) return [];
  if (Array.isArray(value)) {
    return value
      .map((item) => String(item || "").trim())
      .filter(Boolean);
  }
  return String(value)
    .split(/[;,]/)
    .map((item) => item.trim())
    .filter(Boolean);
}

function toPhoneInputValue(value) {
  const raw = String(value || "").trim();
  if (!raw) return "";
  if (raw.startsWith("+")) return raw;
  const digits = raw.replace(/\D/g, "");
  if (!digits) return "";
  if (digits.startsWith("55")) return `+${digits}`;
  return `+55${digits}`;
}

export default function ParceiroDetalhes() {
  const { id } = useParams();
  const navigate = useNavigate();
  const alert = useAppAlert();
  const defaultDateRange = useMemo(() => getCurrentMonthRange(), []);
  const approvalNotificationRefreshRef = useRef({
    partnerId: null,
    execTaskId: null,
    refreshTasks: async () => {},
    refreshChecks: async () => {},
  });
  const approvalNotificationTimeoutRef = useRef(null);
  const fallbackSubmitInFlightRef = useRef(false);

  const [loading, setLoading] = useState(true);
  const [parceiro, setParceiro] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [form, setForm] = useState({
    nome: "",
    documento: "",
    cep: "",
    endereco: "",
    bairro: "",
    cidade: "",
    uf: "",
    nu_endereco: "",
    status: "ativo",
    idJoin: "",
  });

  const [usuarioLogado, setUsuarioLogado] = useState(null);
  const [tiposTarefa, setTiposTarefa] = useState([]);
  const [statusAtividades, setStatusAtividades] = useState([]);
  const [motivosList, setMotivosList] = useState([]);
  const [bancos, setBancos] = useState([]);
  const [convenios, setConvenios] = useState([]);
  const [concorrentes, setConcorrentes] = useState([]);

  const [contatos, setContatos] = useState([]);
  const [showContatoModal, setShowContatoModal] = useState(false);
  const [contatoForm, setContatoForm] = useState({ idContato: null, nomeContato: "" });

  const [showTelefoneModal, setShowTelefoneModal] = useState(false);
  const [contatoSelecionado, setContatoSelecionado] = useState(null);
  const [telefoneForm, setTelefoneForm] = useState({ idTelefone: null, numero: "", tipo: "CELULAR" });

  const [tarefas, setTarefas] = useState([]);
  const [showTarefaModal, setShowTarefaModal] = useState(false);
  const [editingTarefaId, setEditingTarefaId] = useState(null);
  const [editingTarefa, setEditingTarefa] = useState(null);
  const [tarefaForm, setTarefaForm] = useState({
    tipoId: "",
    dataHora: "",
    usuarioId: "",
    motivoIds: [],
    observacoes: "",
  });
  const [savingTarefaModal, setSavingTarefaModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatusIds, setFilterStatusIds] = useState([]);
  const [filterTipoIds, setFilterTipoIds] = useState([]);
  const [filtroDataInicio, setFiltroDataInicio] = useState(defaultDateRange.from);
  const [filtroDataFim, setFiltroDataFim] = useState(defaultDateRange.to);
  // A vida do agente usa o período do filtro da barra superior (o mesmo das tarefas). Data futura
  // é cortada em hoje e filtro vazio cai no mês útil corrente (sanitizeJoinPeriodo).
  const joinPeriodo = useMemo(
    () => sanitizeJoinPeriodo({ dataInicio: filtroDataInicio, dataFim: filtroDataFim }),
    [filtroDataInicio, filtroDataFim]
  );
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [abaAtiva, setAbaAtiva] = useState("informacoes");
  const [motivosExpandidos, setMotivosExpandidos] = useState(new Set());

  const taskScheduleBounds = useMemo(() => buildTaskScheduleBounds(), [showTarefaModal, editingTarefaId]);

  const [showExecModal, setShowExecModal] = useState(false);
  const [execTask, setExecTask] = useState(null);
  const [execReadOnlyMode, setExecReadOnlyMode] = useState(false);
  const [checks, setChecks] = useState([]);
  const [execTaskHistory, setExecTaskHistory] = useState([]);
  const [execTaskHistoryLoading, setExecTaskHistoryLoading] = useState(false);
  const [execLoading, setExecLoading] = useState(false);
  const [concluirForm, setConcluirForm] = useState({
    relatorio: "",
    produto: "",
    comissaoParceiroAjustada: null,
    loginParceiroSolicitado: null,
    bancosConvenios: [createBancoLinha()],
  });
  const [fallbackForm, setFallbackForm] = useState({
    motivo: "",
    files: [],
  });
  const [evidenciaForm, setEvidenciaForm] = useState({
    observacao: "",
    files: [],
  });
  const [fallbackLiberado, setFallbackLiberado] = useState({
    habilitado: false,
    acao: null,
    mensagem: "",
  });
  const [geoCheckDrawer, setGeoCheckDrawer] = useState({
    open: false,
    action: "checkin",
  });
  const [acaoLoading, setAcaoLoading] = useState(false);

  const [visitasRealizadas, setVisitasRealizadas] = useState(0);
  const [atividadesRealizadas, setAtividadesRealizadas] = useState(0);

  const [joinResumo, setJoinResumo] = useState({});
  const [joinResumoAnterior, setJoinResumoAnterior] = useState({});
  const [joinLoading, setJoinLoading] = useState(false);
  const [joinValoresModo, setJoinValoresModo] = useState("media");
  const [historicoPage, setHistoricoPage] = useState(EMPTY_HISTORY_PAGE);
  const [historicoLoading, setHistoricoLoading] = useState(false);
  const [historicoLoadingMore, setHistoricoLoadingMore] = useState(false);
  const [historicoTermo, setHistoricoTermo] = useState("");
  const [historicoTermoAplicado, setHistoricoTermoAplicado] = useState("");
  const [historicoDataInicio, setHistoricoDataInicio] = useState("");
  const [historicoDataFim, setHistoricoDataFim] = useState("");
  const [historicoCategoriasAtivas, setHistoricoCategoriasAtivas] = useState(() =>
    HISTORY_CATEGORY_CONFIG.map((item) => item.key)
  );

  const fallbackPreviews = useMemo(
    () =>
      (fallbackForm.files || []).map((file) => ({
        name: file.name,
        url: URL.createObjectURL(file),
      })),
    [fallbackForm.files]
  );

  const evidenciaPreviews = useMemo(
    () =>
      (evidenciaForm.files || []).map((file) => ({
        name: file.name,
        url: URL.createObjectURL(file),
      })),
    [evidenciaForm.files]
  );

  useEffect(
    () => () => {
      fallbackPreviews.forEach((preview) => URL.revokeObjectURL(preview.url));
    },
    [fallbackPreviews]
  );

  useEffect(
    () => () => {
      evidenciaPreviews.forEach((preview) => URL.revokeObjectURL(preview.url));
    },
    [evidenciaPreviews]
  );

  useEffect(() => {
    if (!showExecModal || !execTask?.id || typeof window === "undefined") return;
    const payload = {
      relatorio: concluirForm.relatorio || "",
      produto: concluirForm.produto || "",
      comissaoParceiroAjustada: concluirForm.comissaoParceiroAjustada,
      loginParceiroSolicitado: concluirForm.loginParceiroSolicitado,
      bancosConvenios: concluirForm.bancosConvenios || [createBancoLinha()],
      fallbackMotivo: fallbackForm.motivo || "",
      evidenciaObservacao: evidenciaForm.observacao || "",
      updatedAt: Date.now(),
    };
    window.sessionStorage.setItem(getConcluirDraftStorageKey(execTask.id), JSON.stringify(payload));
  }, [
    showExecModal,
    execTask?.id,
    concluirForm,
    fallbackForm.motivo,
    evidenciaForm.observacao,
  ]);

  const carregarParceiroAtual = async () => {
    const data = await buscarParceiro(id);
    setParceiro(data);
    setForm(mapParceiroParaForm(data));
  };

  // Cancelar descarta o que foi digitado: antes o modo leitura seguia mostrando a edição abandonada.
  const handleCancelarEdicaoParceiro = () => {
    setForm(mapParceiroParaForm(parceiro));
    setEditMode(false);
  };

  const documentoLabel = useMemo(() => getCpfCnpjLabel(form.documento), [form.documento]);
  const handleDocumentoChange = (value) => {
    setForm((prev) => ({ ...prev, documento: formatCpfCnpj(value) }));
  };

  const handleCepBlur = async () => {
    if (!editMode || !form.cep) return;
    try {
      const endereco = await buscarEnderecoPorCep(form.cep.replace("-", ""));
      if (endereco?.cepGeral) {
        SwalWithTheme.warning(
          "CEP",
          "Este CEP é geral da cidade e não possui logradouro. Informe rua e bairro manualmente."
        );
      }
      setForm((prev) => ({
        ...prev,
        endereco: endereco.logradouro || prev.endereco,
        bairro: endereco.bairro || prev.bairro,
        cidade: endereco.localidade || prev.cidade,
        uf: endereco.uf || prev.uf,
      }));
    } catch {
      SwalWithTheme.error("CEP", "Não foi possível localizar este CEP");
    }
  };

  const carregarContatos = async () => {
    const dados = await listarContatosPorParceiro(id);
    setContatos(Array.isArray(dados) ? dados : []);
  };

  const carregarTarefas = async () => {
    const dados = await listarTarefasPaginado({
      page: 0,
      size: 300,
      parceiroId: id,
      incluirInativas: true,
    });
    setTarefas(Array.isArray(dados?.content) ? dados.content : []);
  };

  const carregarHistoricoParceiro = async ({ page = 0, append = false, silent = false } = {}) => {
    if (!historicoCategoriasAtivas.length) {
      setHistoricoPage({
        ...EMPTY_HISTORY_PAGE,
        size: HISTORY_PAGE_SIZE,
      });
      return;
    }

    if (append) {
      setHistoricoLoadingMore(true);
    } else if (!silent) {
      setHistoricoLoading(true);
    }

    try {
      const dados = await listarHistoricoParceiroService(id, {
        page,
        size: HISTORY_PAGE_SIZE,
        termo: historicoTermoAplicado,
        categorias: historicoCategoriasAtivas,
        dataInicio: historicoDataInicio,
        dataFim: historicoDataFim,
      });

      setHistoricoPage((previous) => ({
        ...dados,
        content: append ? [...(previous.content || []), ...(dados.content || [])] : dados.content || [],
      }));
    } catch {
      // Na recarga automática, falha mantém a lista atual e não alerta: tenta de novo no próximo ciclo.
      if (silent) return;
      if (!append) {
        setHistoricoPage({
          ...EMPTY_HISTORY_PAGE,
          size: HISTORY_PAGE_SIZE,
        });
      }
      SwalWithTheme.error("Erro", "Não foi possível carregar o histórico do parceiro");
    } finally {
      setHistoricoLoading(false);
      setHistoricoLoadingMore(false);
    }
  };

  const carregarDadosBase = async () => {
    const [usuario, tipos, status, motivos, bancosAtivos, conveniosAtivos, concorrentesAtivos] = await Promise.all([
      getUsuarioLogado(),
      listarTiposDeTarefa(),
      listarStatusAtividades(),
      listarMotivosTarefa(),
      listarBancosAtivos(),
      listarConveniosAtivos(),
      listarConcorrentes(),
    ]);
    setUsuarioLogado(usuario);
    setTiposTarefa(Array.isArray(tipos) ? tipos : []);
    setStatusAtividades(Array.isArray(status) ? status : []);
    setMotivosList(Array.isArray(motivos) ? motivos : []);
    setBancos(Array.isArray(bancosAtivos) ? bancosAtivos : []);
    setConvenios(Array.isArray(conveniosAtivos) ? conveniosAtivos : []);
    setConcorrentes(Array.isArray(concorrentesAtivos) ? concorrentesAtivos : []);
    setTarefaForm((prev) => ({ ...prev, usuarioId: usuario?.id || "" }));
  };

  const carregarContadores = async (parceiroAtual) => {
    try {
      const parceiroUserId = parceiroAtual?.usuario?.id || parceiroAtual?.usuarioId;
      if (!parceiroUserId) {
        setVisitasRealizadas(0);
        setAtividadesRealizadas(0);
        return;
      }

      const tipoVisita = tiposTarefa.find((t) => t.nomeTipo?.toLowerCase().includes("visita"));
      const statusConcluida = statusAtividades.find((s) => s.nome?.toLowerCase().includes("conclu"));

      let visitas = 0;
      let atividades = 0;

      if (tipoVisita && statusConcluida) {
        visitas = Number(
          await countTarefasPorUsuarioTipoStatus(parceiroUserId, tipoVisita.id, statusConcluida.id)
        ) || 0;
      }

      if (statusConcluida) {
        atividades = Number(
          await countTarefasPorUsuarioStatus(parceiroUserId, statusConcluida.id)
        ) || 0;
      }

      setVisitasRealizadas(visitas);
      setAtividadesRealizadas(atividades);
    } catch {
      setVisitasRealizadas(0);
      setAtividadesRealizadas(0);
    }
  };

  const carregarResumoJoin = async (parceiroAtual = parceiro, { silent = false } = {}) => {
    const vendedorId = parceiroAtual?.idJoin;
    if (!vendedorId) {
      setJoinResumo({});
      setJoinResumoAnterior({});
      return;
    }

    const periodoAtual = sanitizeJoinPeriodo(joinPeriodo);
    const periodoAnterior = buildJoinPeriodoComparativo(periodoAtual);

    setJoinLoading(true);
    try {
      const [payloadAtual, payloadAnterior] = await Promise.all([
        buscarVidaVendedorResumo({
          vendedorId,
          dataInicio: periodoAtual.dataInicio,
          dataFim: periodoAtual.dataFim,
        }),
        buscarVidaVendedorResumo({
          vendedorId,
          dataInicio: periodoAnterior.dataInicio,
          dataFim: periodoAnterior.dataFim,
        }),
      ]);
      setJoinResumo(payloadAtual || {});
      setJoinResumoAnterior(payloadAnterior || {});
    } catch (error) {
      // A Join oscila: na recarga automática, mantém os números atuais em vez de zerar e alertar.
      if (silent) return;
      const message = error?.response?.data?.message || "Não foi possível carregar a vida do agente";
      SwalWithTheme.warning("JoinConsig", message);
      setJoinResumo({});
      setJoinResumoAnterior({});
    } finally {
      setJoinLoading(false);
    }
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        await carregarDadosBase();
        await carregarParceiroAtual();
        await Promise.all([carregarContatos(), carregarTarefas()]);
      } catch {
        SwalWithTheme.error("Erro", "Falha ao carregar dados do parceiro");
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  useEffect(() => {
    const savedTaskFilters = loadTaskFiltersFromSession(id);
    setSearchTerm(savedTaskFilters.searchTerm);
    setFilterStatusIds(savedTaskFilters.filterStatusIds);
    setFilterTipoIds(savedTaskFilters.filterTipoIds);
    setFiltroDataInicio(savedTaskFilters.filtroDataInicio);
    setFiltroDataFim(savedTaskFilters.filtroDataFim);
    setFiltersOpen(false);
    setHistoricoPage(EMPTY_HISTORY_PAGE);
    setHistoricoTermo("");
    setHistoricoTermoAplicado("");
    setHistoricoDataInicio("");
    setHistoricoDataFim("");
    setHistoricoCategoriasAtivas(HISTORY_CATEGORY_CONFIG.map((item) => item.key));
  }, [id]);

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      setHistoricoTermoAplicado(historicoTermo.trim());
    }, 250);

    return () => window.clearTimeout(timeout);
  }, [historicoTermo]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    window.sessionStorage.setItem(
      `${TASK_FILTER_STORAGE_PREFIX}${id}`,
      JSON.stringify({
        searchTerm,
        filterStatusIds,
        filterTipoIds,
        filtroDataInicio,
        filtroDataFim,
      })
    );
  }, [id, searchTerm, filterStatusIds, filterTipoIds, filtroDataInicio, filtroDataFim]);


  useEffect(() => {
    if (!parceiro) return;
    carregarContadores(parceiro);
  }, [parceiro, tiposTarefa, statusAtividades]);

  // Recarrega a vida do agente sozinha quando o período muda. A espera curta evita uma consulta à
  // Join por tecla enquanto a data é digitada.
  useEffect(() => {
    if (!parceiro) return undefined;
    const timeout = window.setTimeout(() => carregarResumoJoin(parceiro), 400);
    return () => window.clearTimeout(timeout);
  }, [parceiro, joinPeriodo.dataInicio, joinPeriodo.dataFim]);

  useEffect(() => {
    carregarHistoricoParceiro({ page: 0, append: false });
  }, [id, historicoTermoAplicado, historicoDataInicio, historicoDataFim, historicoCategoriasAtivas.join("|")]);

  const autoRefreshRef = useRef(null);
  autoRefreshRef.current = ({ manual = false } = {}) => {
    // Não recarrega com a aba do navegador escondida, com modal aberto ou no meio de uma edição:
    // trocar a lista por baixo de quem está digitando confunde. O clique no ícone da barra força.
    const pausado = document.hidden || editMode || showTarefaModal || showExecModal || showContatoModal || showTelefoneModal;
    if ((pausado && !manual) || !parceiro) return;
    carregarResumoJoin(parceiro, { silent: true });
    carregarTarefas().catch(() => {});
    // Com "Carregar mais" aberto, recarregar a página 0 recolheria a lista.
    if (!Number(historicoPage.page || 0)) {
      carregarHistoricoParceiro({ page: 0, append: false, silent: true });
    }
  };

  // Contagem regressiva exibida na barra superior, igual ao dashboard (services/dashboardRefreshService).
  const [refreshCountdown, setRefreshCountdown] = useState(AUTO_REFRESH_SECONDS);

  useEffect(() => {
    setRefreshCountdown(AUTO_REFRESH_SECONDS);
    const intervalId = window.setInterval(() => {
      setRefreshCountdown((current) => Math.max(0, current - 1));
    }, 1000);

    // Clique no ícone de atualizar da barra superior: recarrega já e reinicia a contagem.
    const handleRefreshRequest = () => {
      autoRefreshRef.current?.({ manual: true });
      setRefreshCountdown(AUTO_REFRESH_SECONDS);
    };
    window.addEventListener(DASHBOARD_REFRESH_REQUEST_EVENT, handleRefreshRequest);

    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener(DASHBOARD_REFRESH_REQUEST_EVENT, handleRefreshRequest);
      clearDashboardRefreshState();
    };
  }, [id]);

  // Recarga fora do updater do setState: em StrictMode o updater roda duas vezes.
  useEffect(() => {
    if (refreshCountdown > 0) return;
    autoRefreshRef.current?.();
    setRefreshCountdown(AUTO_REFRESH_SECONDS);
  }, [refreshCountdown]);

  useEffect(() => {
    publishDashboardRefreshState({
      visible: true,
      loading: joinLoading,
      countdown: refreshCountdown,
      countdownLabel: formatRefreshCountdown(refreshCountdown),
    });
  }, [joinLoading, refreshCountdown]);

  const toggleHistoricoCategoria = (categoriaKey) => {
    setHistoricoCategoriasAtivas((previous) => {
      if (previous.includes(categoriaKey)) {
        return previous.filter((item) => item !== categoriaKey);
      }
      return [...previous, categoriaKey];
    });
  };

  const handleClearHistoryFilters = () => {
    setHistoricoTermo("");
    setHistoricoDataInicio("");
    setHistoricoDataFim("");
    setHistoricoCategoriasAtivas(HISTORY_CATEGORY_CONFIG.map((item) => item.key));
  };

  const handleSalvarParceiro = async () => {
    if (form.status === "ativo" && !form.idJoin) {
      SwalWithTheme.error("Erro", "ID Join e obrigatorio para parceiros ativos");
      return;
    }
    try {
      let formAtual = { ...form };
      const cepAtual = String(form.cep || "").replace(/\D/g, "");
      const cepAnterior = String(parceiro?.cep || "").replace(/\D/g, "");

      if (cepAtual.length === 8 && cepAtual !== cepAnterior) {
        try {
          const enderecoCep = await buscarEnderecoPorCep(cepAtual);
          if (enderecoCep?.cepGeral) {
            SwalWithTheme.warning(
              "CEP",
              "Este CEP é geral da cidade e não possui logradouro. Informe rua e bairro manualmente."
            );
          }
          formAtual = {
            ...formAtual,
            endereco: enderecoCep?.logradouro || formAtual.endereco,
            bairro: enderecoCep?.bairro || formAtual.bairro,
            cidade: enderecoCep?.localidade || formAtual.cidade,
            uf: enderecoCep?.uf || formAtual.uf,
          };
          setForm((prev) => ({
            ...prev,
            endereco: formAtual.endereco,
            bairro: formAtual.bairro,
            cidade: formAtual.cidade,
            uf: formAtual.uf,
          }));
        } catch {
          // Mantem os valores digitados caso o CEP nao seja resolvido neste momento.
        }
      }

      await atualizarParceiro(id, {
        idJoin: formAtual.idJoin ? Number(formAtual.idJoin) : null,
        nome: formAtual.nome,
        documento: formAtual.documento,
        endereco: formAtual.endereco,
        status: formAtual.status,
        cep: formAtual.cep,
        bairro: formAtual.bairro,
        cidade: formAtual.cidade,
        uf: formAtual.uf,
        nu_endereco: formAtual.nu_endereco,
        usuarioId: parceiro?.usuario?.id ?? parceiro?.usuarioId ?? null,
      });
      await carregarParceiroAtual();
      await carregarHistoricoParceiro({ page: 0, append: false });
      setEditMode(false);
      SwalWithTheme.success("Sucesso", "Parceiro atualizado com sucesso");
    } catch (error) {
      const message = error?.response?.data?.message || "Erro ao atualizar parceiro";
      SwalWithTheme.error("Erro", message);
    }
  };

  const handleSalvarContato = async () => {
    if (!contatoForm.nomeContato.trim()) {
      SwalWithTheme.error("Erro", "Nome do contato e obrigatorio");
      return;
    }

    try {
      if (contatoForm.idContato) {
        await atualizarContato(contatoForm.idContato, {
          nomeContato: contatoForm.nomeContato,
          idParceiro: id,
        });
      } else {
        await salvarContato({ nomeContato: contatoForm.nomeContato, idParceiro: id });
      }
      await carregarContatos();
      setShowContatoModal(false);
      setContatoForm({ idContato: null, nomeContato: "" });
      SwalWithTheme.success("Sucesso", "Contato salvo com sucesso");
    } catch {
      SwalWithTheme.error("Erro", "Erro ao salvar contato");
    }
  };

  const handleRemoverContato = async (contatoId) => {
    const result = await SwalWithTheme.confirm(
      "Confirmar exclusoo",
      "Tem certeza que deseja remover este contato?",
      "Sim, remover",
      "Cancelar"
    );
    if (!result.isConfirmed) return;

    try {
      await removerContato(contatoId);
      await carregarContatos();
      SwalWithTheme.success("Sucesso", "Contato removido com sucesso");
    } catch {
      SwalWithTheme.error("Erro", "Erro ao remover contato");
    }
  };

  const handleAdicionarTelefone = (contato) => {
    setContatoSelecionado(contato);
    setTelefoneForm({ idTelefone: null, numero: "", tipo: "CELULAR" });
    setShowTelefoneModal(true);
  };

  const handleSalvarTelefone = async () => {
    if (!contatoSelecionado) {
      SwalWithTheme.error("Erro", "Selecione um contato");
      return;
    }
    if (!telefoneForm.numero.trim()) {
      SwalWithTheme.error("Erro", "Numero do telefone e obrigatorio");
      return;
    }
    if (!isPossiblePhoneNumber(telefoneForm.numero)) {
      SwalWithTheme.error("Erro", "Numero de telefone invalido");
      return;
    }

    try {
      const tipo = telefoneForm.tipo || inferPhoneType(telefoneForm.numero);
      const numeroNormalizado = telefoneForm.numero.replace(/\D/g, "");
      if (telefoneForm.idTelefone) {
        await atualizarTelefone(telefoneForm.idTelefone, {
          telefone: numeroNormalizado,
          tipo,
          idContato: contatoSelecionado.idContato,
        });
      } else {
        await salvarTelefone({
          telefone: numeroNormalizado,
          tipo,
          idContato: contatoSelecionado.idContato,
        });
      }
      await carregarContatos();
      setShowTelefoneModal(false);
      setTelefoneForm({ idTelefone: null, numero: "", tipo: "CELULAR" });
      SwalWithTheme.success("Sucesso", "Telefone salvo com sucesso");
    } catch {
      SwalWithTheme.error("Erro", "Erro ao salvar telefone");
    }
  };

  const handleRemoverTelefone = async (telefoneId) => {
    const result = await SwalWithTheme.confirm(
      "Confirmar exclusoo",
      "Tem certeza que deseja remover este telefone?",
      "Sim, remover",
      "Cancelar"
    );
    if (!result.isConfirmed) return;

    try {
      await removerTelefone(telefoneId);
      await carregarContatos();
      SwalWithTheme.success("Sucesso", "Telefone removido com sucesso");
    } catch {
      SwalWithTheme.error("Erro", "Erro ao remover telefone");
    }
  };

  const getStatusNome = (task) => {
    const status = statusAtividades.find((item) => String(item.id) === String(task?.idStatusAtividade || ""));
    return String(status?.nome || "").trim().toLowerCase();
  };

  const isTaskConcluida = (task) => {
    if (!task) return false;
    if (task.dataConclusao) return true;
    return getStatusNome(task).includes("conclu");
  };

  const isTaskCancelada = (task) => {
    if (!task) return false;
    return getStatusNome(task).includes("cancel");
  };

  const podeEditarDataHora = (task) => {
    if (!usuarioLogado) return false;
    if (isTaskConcluida(task) || isTaskCancelada(task)) return false;
    const isAdmin = String(usuarioLogado.cargo || "").toLowerCase() === "admin";
    if (isAdmin) return true;
    return String(task.idUsuarioCriador || "") === String(usuarioLogado.id || "");
  };

  const podeEditar = (task) => {
    if (!usuarioLogado || !task) return false;
    if (isTaskConcluida(task) || isTaskCancelada(task)) return false;
    const isAdminCargo = String(usuarioLogado.cargo || "").toLowerCase() === "admin";
    if (isAdminCargo) return true;
    return String(task.idUsuario || "") === String(usuarioLogado.id || "");
  };

  const podeCancelar = (task) => {
    if (!usuarioLogado || !task) return false;
    if (isTaskConcluida(task) || isTaskCancelada(task)) return false;
    const isAdminCargo = String(usuarioLogado.cargo || "").toLowerCase() === "admin";
    if (isAdminCargo) return true;
    return (
      String(task.idUsuario || "") === String(usuarioLogado.id || "") &&
      String(task.idUsuarioCriador || "") === String(usuarioLogado.id || "")
    );
  };

  const podeConcluir = (task) => {
    if (!usuarioLogado || !task) return false;
    if (isTaskConcluida(task) || isTaskCancelada(task)) return false;
    const isAdminCargo = String(usuarioLogado.cargo || "").toLowerCase() === "admin";
    if (isAdminCargo) return true;
    return String(task.idUsuario || "") === String(usuarioLogado.id || "");
  };

  const statusOptionMap = useMemo(() => {
    const map = new Map();
    statusAtividades.forEach((s) => map.set(String(s.id), s));
    return map;
  }, [statusAtividades]);

  const motivosMap = useMemo(() => {
    const map = new Map();
    motivosList.forEach((m) => map.set(String(m.id), m));
    return map;
  }, [motivosList]);

  const tiposMap = useMemo(() => {
    const map = new Map();
    tiposTarefa.forEach((tipo) => map.set(String(tipo.id), tipo));
    return map;
  }, [tiposTarefa]);

  const getLinhaMotivoKey = (row) => String(row?.id ?? row?.idTarefa ?? row?.codigo ?? "");

  const toggleMotivosExpandidos = (row) => {
    const key = getLinhaMotivoKey(row);
    if (!key) return;
    setMotivosExpandidos((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const bancosMap = useMemo(() => {
    const map = new Map();
    bancos.forEach((b) => {
      const key = String(b.bancoId ?? b.BancoId ?? "");
      if (key) map.set(key, b);
    });
    return map;
  }, [bancos]);

  const conveniosMap = useMemo(() => {
    const map = new Map();
    convenios.forEach((c) => {
      const key = String(c.convenioId ?? c.ConvenioId ?? "");
      if (key) map.set(key, c);
    });
    return map;
  }, [convenios]);

  const concorrentesMap = useMemo(() => {
    const map = new Map();
    concorrentes.forEach((concorrente) => {
      const key = String(concorrente?.id ?? "");
      if (key) map.set(key, concorrente);
    });
    return map;
  }, [concorrentes]);

  const handleAdicionarTarefa = () => {
    setEditingTarefaId(null);
    setEditingTarefa(null);
    const responsavelId = parceiro?.usuario?.id || parceiro?.usuarioId || usuarioLogado?.id;
    setTarefaForm({
      tipoId: "",
      dataHora: "",
      usuarioId: responsavelId || "",
      motivoIds: [],
      observacoes: "",
    });
    setShowTarefaModal(true);
  };

  const handleEditarTarefa = (tarefa) => {
    if (!podeEditar(tarefa)) {
      SwalWithTheme.error("Acao nao permitida", "Tarefas concluidas ou canceladas nao podem ser editadas.");
      return;
    }

    setEditingTarefaId(tarefa.id);
    setEditingTarefa(tarefa);
    const motivoIdsDaTarefa = (tarefa.motivosIds || []).map((motivoId) => String(motivoId));
    const fallbackMotivoIds = motivoIdsDaTarefa.length
      ? motivoIdsDaTarefa
      : parseMotivos(tarefa.motivo)
          .map((nomeMotivo) =>
            motivosList.find((motivo) => normalizeKey(motivo.nome) === normalizeKey(nomeMotivo))?.id
          )
          .filter(Boolean)
          .map(String);

    setTarefaForm({
      tipoId: String(tarefa.idTipoDeTarefa || tarefa.tipoDeTarefa?.id || ""),
      dataHora: toDateTimeLocal(tarefa.dataInicio),
      usuarioId: String(tarefa.idUsuario || tarefa.usuario?.id || ""),
      motivoIds: fallbackMotivoIds,
      observacoes: tarefa.observacoes || tarefa.descricaoTarefa || "",
    });
    setShowTarefaModal(true);
  };

  const handleSalvarTarefaParceiro = async () => {
    if (savingTarefaModal) return;

    const motivoIds = (tarefaForm.motivoIds || []).map(String);
    if (!tarefaForm.tipoId || !tarefaForm.dataHora || motivoIds.length === 0) {
      SwalWithTheme.error("Erro", "Preencha tipo, data/hora e ao menos um motivo");
      return;
    }
    if (isTaskScheduleOutsideWindow(tarefaForm.dataHora, taskScheduleBounds)) {
      SwalWithTheme.error(
        "Erro",
        `A data/hora da tarefa deve estar entre ${TASK_SCHEDULE_PAST_WINDOW_DAYS} dias no passado e ${TASK_SCHEDULE_WINDOW_DAYS} dias no futuro`
      );
      return;
    }
    if (editingTarefaId && editingTarefa && !podeEditar(editingTarefa)) {
      SwalWithTheme.error("Acao nao permitida", "Essa tarefa nao pode ser editada no seu perfil.");
      return;
    }
    if (
      editingTarefa &&
      !podeEditarDataHora(editingTarefa) &&
      normalizeDateTime(tarefaForm.dataHora) !== normalizeDateTime(toDateTimeLocal(editingTarefa.dataInicio))
    ) {
      SwalWithTheme.error("Erro", "Data/hora nao pode ser alterada para tarefa atribuida por administrador");
      return;
    }

    try {
      setSavingTarefaModal(true);
      const payload = {
        descricaoTarefa: buildTaskTitleFromNotes(tarefaForm.observacoes),
        dataInicio: normalizeDateTime(tarefaForm.dataHora),
        motivosIds: motivoIds.map((motivoId) => Number(motivoId)),
        observacoes: tarefaForm.observacoes,
        idTipoDeTarefa: Number(tarefaForm.tipoId),
        parceirosIds: [Number(id)],
      };

      if (editingTarefaId) {
        await atualizarTarefaService(editingTarefaId, payload);
        SwalWithTheme.success("Sucesso", "Tarefa atualizada");
      } else {
        await criarTarefaService(payload);
        SwalWithTheme.success("Sucesso", "Tarefa criada");
      }

      await carregarTarefas();
      await carregarHistoricoParceiro({ page: 0, append: false });
      setShowTarefaModal(false);
      setEditingTarefa(null);
      setEditingTarefaId(null);
    } catch (error) {
      const message = error?.response?.data?.message || "Erro ao salvar tarefa";
      SwalWithTheme.error("Erro", message);
    } finally {
      setSavingTarefaModal(false);
    }
  };

  const handleExcluirTarefa = async (tarefa) => {
    if (!podeCancelar(tarefa)) {
      SwalWithTheme.error(
        "Acao nao permitida",
        "Essa tarefa atribuida por administrador nao pode ser cancelada pelo comercial."
      );
      return;
    }
    const result = await SwalWithTheme.confirm(
      "Confirmar cancelamento",
      "Deseja cancelar esta tarefa?",
      "Cancelar tarefa",
      "Voltar"
    );
    if (!result.isConfirmed) return;

    try {
      await removerTarefaService(tarefa.id);
      await carregarTarefas();
      await carregarHistoricoParceiro({ page: 0, append: false });
      SwalWithTheme.success("Sucesso", "Tarefa cancelada");
    } catch (error) {
      const message = error?.response?.data?.message || "Erro ao cancelar tarefa";
      SwalWithTheme.error("Erro", message);
    }
  };

  const carregarChecks = async (taskId) => {
    const data = await listarChecksTarefa(taskId);
    setChecks(Array.isArray(data) ? data : []);
  };

  const carregarHistoricoTarefaExecucao = async (taskId) => {
    if (!taskId) {
      setExecTaskHistory([]);
      return;
    }
    setExecTaskHistoryLoading(true);
    try {
      const data = await listarHistoricoTarefa(taskId);
      setExecTaskHistory(Array.isArray(data) ? data : []);
    } catch {
      setExecTaskHistory([]);
      SwalWithTheme.error("Erro", "Não foi possível carregar o histórico da tarefa");
    } finally {
      setExecTaskHistoryLoading(false);
    }
  };

  useEffect(() => {
    approvalNotificationRefreshRef.current = {
      partnerId: id,
      execTaskId: execTask?.id || null,
      refreshTasks: () => carregarTarefas(),
      refreshChecks: (taskId) => carregarChecks(taskId),
    };
  });

  useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }

    const handleNotificationCreated = (event) => {
      const notification = event?.detail;
      if (!isApprovalNotification(notification)) {
        return;
      }

      const current = approvalNotificationRefreshRef.current;
      const taskId = getNotificationTaskId(notification);
      const partnerIds = getNotificationPartnerIds(notification);
      const currentPartnerId = String(current.partnerId || "");
      const affectsCurrentTask = current.execTaskId != null && taskId != null && String(current.execTaskId) === String(taskId);
      const affectsCurrentPartner =
        currentPartnerId.length > 0
        && partnerIds.some((partnerId) => String(partnerId) === currentPartnerId);

      if (!affectsCurrentTask && !affectsCurrentPartner) {
        return;
      }

      if (approvalNotificationTimeoutRef.current) {
        window.clearTimeout(approvalNotificationTimeoutRef.current);
      }

      approvalNotificationTimeoutRef.current = window.setTimeout(() => {
        approvalNotificationTimeoutRef.current = null;

        if (affectsCurrentPartner) {
          void current.refreshTasks();
        }

        if (affectsCurrentTask) {
          void current.refreshChecks(current.execTaskId);
        }
      }, 350);
    };

    window.addEventListener(APP_NOTIFICATION_CREATED_EVENT, handleNotificationCreated);
    return () => {
      window.removeEventListener(APP_NOTIFICATION_CREATED_EVENT, handleNotificationCreated);
      if (approvalNotificationTimeoutRef.current) {
        window.clearTimeout(approvalNotificationTimeoutRef.current);
        approvalNotificationTimeoutRef.current = null;
      }
    };
  }, []);

  const abrirExecucao = async (tarefa, { readOnly = false } = {}) => {
    try {
      setExecLoading(true);
      setAcaoLoading(false);
      setExecReadOnlyMode(readOnly);
      setExecTask(tarefa);

      const dadosFinanceiros = parseJsonSafe(tarefa.dadosFinanceiros, []);
      const bancosConvenios = mapDadosFinanceirosParaLinhas(dadosFinanceiros).map((linha) => ({
        ...createBancoLinha(),
        ...linha,
        bancoId: String(linha.bancoId || ""),
        convenioId: String(linha.convenioId || ""),
        valorProduzido: formatMoneyBRL(linha.valorProduzido),
        concorrenteId: String(linha.concorrenteId || ""),
        concorrenteOutro: String(linha.concorrenteOutro || ""),
      }));

      const defaultConcluirForm = {
        relatorio: tarefa.relatorio || "",
        produto: tarefa.produto || "",
        comissaoParceiroAjustada:
          typeof tarefa.comissaoParceiroAjustada === "boolean" ? tarefa.comissaoParceiroAjustada : null,
        loginParceiroSolicitado:
          typeof tarefa.loginParceiroSolicitado === "boolean" ? tarefa.loginParceiroSolicitado : null,
        bancosConvenios: bancosConvenios.length ? bancosConvenios : [createBancoLinha()],
      };
      const draft = loadConcluirDraft(tarefa.id);

      setConcluirForm(
        draft
          ? {
              relatorio: draft.relatorio || defaultConcluirForm.relatorio,
              produto: draft.produto || defaultConcluirForm.produto,
              comissaoParceiroAjustada:
                typeof draft.comissaoParceiroAjustada === "boolean"
                  ? draft.comissaoParceiroAjustada
                  : defaultConcluirForm.comissaoParceiroAjustada,
              loginParceiroSolicitado:
                typeof draft.loginParceiroSolicitado === "boolean"
                  ? draft.loginParceiroSolicitado
                  : defaultConcluirForm.loginParceiroSolicitado,
              bancosConvenios: Array.isArray(draft.bancosConvenios) && draft.bancosConvenios.length
                ? draft.bancosConvenios.map((linha) => ({
                    ...createBancoLinha(),
                    ...linha,
                    valorProduzido: formatMoneyBRL(linha?.valorProduzido),
                  }))
                : defaultConcluirForm.bancosConvenios,
            }
          : defaultConcluirForm
      );
      setFallbackForm({ motivo: draft?.fallbackMotivo || "", files: [] });
      setEvidenciaForm({ observacao: draft?.evidenciaObservacao || "", files: [] });
      setFallbackLiberado({
        habilitado: false,
        acao: null,
        mensagem: "",
      });

      await Promise.all([
        carregarChecks(tarefa.id),
        carregarHistoricoTarefaExecucao(tarefa.id),
      ]);
      setShowExecModal(true);
    } catch (error) {
      setExecReadOnlyMode(false);
      const message = error?.response?.data?.message || "Erro ao abrir execucao da tarefa";
      SwalWithTheme.error("Erro", message);
    } finally {
      setExecLoading(false);
    }
  };

  const abrirTarefaPeloHistorico = async (evento) => {
    if (!evento?.tarefaId) return;
    const tarefaExistente = tarefas.find((item) => String(item.id) === String(evento.tarefaId));
    const tarefaAtual = (await buscarTarefa(evento.tarefaId)) || tarefaExistente;
    if (!tarefaAtual) {
      SwalWithTheme.error("Erro", "Não foi possível localizar a tarefa vinculada a este histórico");
      return;
    }
    await abrirExecucao(tarefaAtual, { readOnly: true });
  };

  const tipoExecucao = useMemo(() => {
    if (!execTask) return null;
    return tiposTarefa.find((t) => String(t.id) === String(execTask.idTipoDeTarefa)) || null;
  }, [execTask, tiposTarefa]);

  const fallbackPolicy = useMemo(() => getStepPolicy(tipoExecucao, "fallback"), [tipoExecucao]);
  const evidencePolicy = useMemo(() => getStepPolicy(tipoExecucao, "evidence"), [tipoExecucao]);
  const checkinExigido = requiresCheckin(tipoExecucao);
  const checkoutExigido = requiresCheckout(tipoExecucao);
  const permiteFallback = isStepEnabled(fallbackPolicy);
  const fallbackExigeImagem = stepRequiresImage(fallbackPolicy);
  const fallbackExigeAprovacao = stepRequiresApproval(fallbackPolicy);
  const exigeEvidencia = isStepEnabled(evidencePolicy);
  const evidenciaExigeImagem = stepRequiresImage(evidencePolicy);
  const evidenciaExigeAprovacao = stepRequiresApproval(evidencePolicy);

  const checkinOk = useMemo(
    () => checks.some((c) => c.acao === "CHECKIN" && ["OK", "APROVADO"].includes(c.status)),
    [checks]
  );

  const checkoutOk = useMemo(
    () => checks.some((c) => c.acao === "CHECKOUT" && ["OK", "APROVADO"].includes(c.status)),
    [checks]
  );

  const evidenciaOk = useMemo(
    () => checks.some((c) => c.acao === "EVIDENCIA_CONCLUSAO" && ["OK", "APROVADO"].includes(c.status)),
    [checks]
  );

  const checkEvidencia = useMemo(
    () => checks.find((c) => c.acao === "EVIDENCIA_CONCLUSAO" && ["OK", "APROVADO", "PENDENTE_APROVACAO"].includes(c.status)),
    [checks]
  );

  const checkinPendente = useMemo(
    () => checks.some((c) => c.acao === "CHECKIN" && c.status === "PENDENTE_APROVACAO"),
    [checks]
  );

  const checkoutPendente = useMemo(
    () => checks.some((c) => c.acao === "CHECKOUT" && c.status === "PENDENTE_APROVACAO"),
    [checks]
  );

  const evidenciaPendente = useMemo(
    () => checks.some((c) => c.acao === "EVIDENCIA_CONCLUSAO" && c.status === "PENDENTE_APROVACAO"),
    [checks]
  );
  const execTaskGeoPartner = useMemo(
    () => buildTaskGeoPartner({ task: execTask, partner: parceiro }),
    [execTask, parceiro]
  );

  const motivosDaExecucao = useMemo(() => {
    if (!execTask) return [];
    const porIds = (execTask.motivosIds || []).map((motivoId) => motivosMap.get(String(motivoId))).filter(Boolean);
    if (porIds.length) return porIds;

    const raw = String(execTask.motivo || "");
    if (!raw.trim()) return [];
    const mapPorNome = new Map(motivosList.map((motivo) => [normalizeKey(motivo.nome), motivo]));
    return raw
      .split(",")
      .map((nome) => mapPorNome.get(normalizeKey(nome)))
      .filter(Boolean);
  }, [execTask, motivosMap, motivosList]);

  const fieldModes = useMemo(() => mergeFieldModes(motivosDaExecucao), [motivosDaExecucao]);
  const bancoFieldMode = fieldModes[TASK_FIELD_CODES.BANCO_NEGOCIADO];
  const produtoFieldMode = fieldModes[TASK_FIELD_CODES.PRODUTO_NEGOCIADO];
  const convenioFieldMode = fieldModes[TASK_FIELD_CODES.CONVENIO_NEGOCIADO];
  const valorFieldMode = fieldModes[TASK_FIELD_CODES.VALOR_PRODUZIDO];
  const concorrenteFieldMode = fieldModes[TASK_FIELD_CODES.CONCORRENTE];

  const exibirBancoNegociado = isFieldVisible(bancoFieldMode);
  const exibirProdutoNegociado = isFieldVisible(produtoFieldMode);
  const exibirConvenioNegociado = isFieldVisible(convenioFieldMode);
  const exibirValorProduzido = isFieldVisible(valorFieldMode);
  const exibirConcorrente = isFieldVisible(concorrenteFieldMode);

  const bancoObrigatorio = isFieldRequired(bancoFieldMode);
  const produtoObrigatorio = isFieldRequired(produtoFieldMode);
  const convenioObrigatorio = isFieldRequired(convenioFieldMode);
  const valorObrigatorio = isFieldRequired(valorFieldMode);
  const concorrenteObrigatorio = isFieldRequired(concorrenteFieldMode);

  const algumCampoFinanceiroHabilitado = hasVisibleField(fieldModes, [
    TASK_FIELD_CODES.BANCO_NEGOCIADO,
    TASK_FIELD_CODES.PRODUTO_NEGOCIADO,
    TASK_FIELD_CODES.CONVENIO_NEGOCIADO,
    TASK_FIELD_CODES.VALOR_PRODUZIDO,
    TASK_FIELD_CODES.CONCORRENTE,
  ]);
  const algumCampoLinhaHabilitado = hasVisibleField(fieldModes, [
    TASK_FIELD_CODES.BANCO_NEGOCIADO,
    TASK_FIELD_CODES.CONVENIO_NEGOCIADO,
    TASK_FIELD_CODES.VALOR_PRODUZIDO,
    TASK_FIELD_CODES.CONCORRENTE,
  ]);

  const conveniosOptions = useMemo(
    () =>
      convenios
        .map((c) => ({
          value: String(c.convenioId || c.ConvenioId || ""),
          label: c.convenioNome || c.ConvenioNome || String(c.convenioId || c.ConvenioId || ""),
        }))
        .filter((option) => option.value),
    [convenios]
  );

  const concorrentesOptions = useMemo(() => {
    const options = (concorrentes || [])
      .map((concorrente) => ({
        value: String(concorrente.id || ""),
        label: concorrente.nome || String(concorrente.id || ""),
      }))
      .filter((option) => option.value);
    return [...options, { value: "OUTRO", label: "Outro" }];
  }, [concorrentes]);

  const handleGeoCheckConfirm = async ({ action, geo }) => {
    if (!execTask || acaoLoading) return;
    try {
      setAcaoLoading(true);
      if (action === "checkin") {
        await checkinTarefa(execTask.id, geo);
      } else {
        await checkoutTarefa(execTask.id, geo);
      }
      await carregarChecks(execTask.id);
      await carregarHistoricoTarefaExecucao(execTask.id);
      await carregarHistoricoParceiro({ page: 0, append: false });
      setFallbackLiberado({
        habilitado: false,
        acao: null,
        mensagem: "",
      });
      setGeoCheckDrawer((current) => ({ ...current, open: false }));
      SwalWithTheme.success("Sucesso", action === "checkin" ? "Check-in registrado" : "Check-out registrado");
    } catch (error) {
      const message = error?.response?.data?.message || error?.message || "Falha ao executar check";
      SwalWithTheme.error("Erro", message);
      if (permiteFallback) {
        const geoInfo = parseGeoErrorInfo(message);
        const mensagemFallback = geoInfo?.distancia
          ? `Distancia atual ${geoInfo.distancia} m${geoInfo.raio ? ` (raio maximo ${geoInfo.raio} m)` : ""}.`
          : message;
        setFallbackLiberado({
          habilitado: true,
          acao: action,
          mensagem: mensagemFallback,
        });
        setFallbackForm((prev) => {
          if (prev.motivo?.trim()) return prev;
          const base = geoInfo?.distancia
            ? `Tentativa fora do raio. Distancia atual ${geoInfo.distancia} m${geoInfo.raio ? ` e raio maximo ${geoInfo.raio} m` : ""}.`
            : "Tentativa de check por geolocalizacao sem sucesso.";
          return { ...prev, motivo: base };
        });
        alert.warning("Check com falha", "Fallback liberado para essa acao apos a falha no check.", {
          position: "top-right",
          duration: 4200,
        });
      }
    } finally {
      setAcaoLoading(false);
    }
  };

  const handleSubmitFallback = async ({ action } = {}) => {
    if (!execTask || acaoLoading || fallbackSubmitInFlightRef.current) return;
    const acao = action || fallbackLiberado.acao;
    const motivo = (fallbackForm.motivo || "").trim();
    const files = Array.isArray(fallbackForm.files) ? fallbackForm.files : [];

    if (!motivo) {
      SwalWithTheme.error("Erro", "Informe o motivo do fallback");
      return;
    }
    if (!fallbackLiberado.habilitado || !acao) {
      SwalWithTheme.error("Erro", "Fallback so fica disponivel apos falha no check");
      return;
    }
    if (fallbackExigeImagem && files.length === 0) {
      SwalWithTheme.error("Erro", "Esse fallback exige ao menos uma imagem");
      return;
    }

    try {
      fallbackSubmitInFlightRef.current = true;
      setAcaoLoading(true);
      const payload = { motivo, files };
      if (acao === "checkin") {
        await fallbackCheckinTarefa(execTask.id, payload);
      } else {
        await fallbackCheckoutTarefa(execTask.id, payload);
      }
      await carregarChecks(execTask.id);
      await carregarHistoricoTarefaExecucao(execTask.id);
      await carregarHistoricoParceiro({ page: 0, append: false });
      SwalWithTheme.success(
        "Sucesso",
        fallbackExigeAprovacao
          ? "Fallback enviado e aguardando aprovacao do admin"
          : "Fallback registrado com sucesso"
      );
      setGeoCheckDrawer((current) => ({ ...current, open: false }));
      setFallbackForm((prev) => ({ ...prev, motivo: "", files: [] }));
      setFallbackLiberado({
        habilitado: false,
        acao: null,
        mensagem: "",
      });
    } catch (error) {
      const message = error?.response?.data?.message || "Falha ao enviar fallback";
      SwalWithTheme.error("Erro", message);
    } finally {
      fallbackSubmitInFlightRef.current = false;
      setAcaoLoading(false);
    }
  };

  useEffect(() => {
    if (!showExecModal) {
      setGeoCheckDrawer((current) => ({ ...current, open: false }));
    }
  }, [showExecModal]);

  const enviarEvidencia = async () => {
    if (!execTask || acaoLoading) return;
    const observacao = (evidenciaForm.observacao || "").trim();
    const files = Array.isArray(evidenciaForm.files) ? evidenciaForm.files : [];
    if (evidenciaExigeImagem && files.length === 0) {
      SwalWithTheme.error("Erro", "Esse tipo exige ao menos uma imagem na evidencia");
      return;
    }

    try {
      setAcaoLoading(true);
      await evidenciarTarefa(execTask.id, {
        observacao,
        files,
      });
      await carregarChecks(execTask.id);
      await carregarHistoricoTarefaExecucao(execTask.id);
      await carregarHistoricoParceiro({ page: 0, append: false });
      SwalWithTheme.success(
        "Sucesso",
        evidenciaExigeAprovacao
          ? "Evidencia enviada e aguardando aprovacao do admin"
          : "Evidencia registrada com sucesso"
      );
      setEvidenciaForm({ observacao: "", files: [] });
    } catch (error) {
      const message = error?.response?.data?.message || "Falha ao enviar evidencia";
      SwalWithTheme.error("Erro", message);
    } finally {
      setAcaoLoading(false);
    }
  };

  const montarDadosFinanceiros = () => {
    return (concluirForm.bancosConvenios || [])
      .map((linha) => ({
        bancoId: String(linha?.bancoId || "").trim(),
        convenioId: String(linha?.convenioId || "").trim(),
        valorProduzido: String(linha?.valorProduzido || "").trim(),
        concorrenteId: String(linha?.concorrenteId || "").trim(),
        concorrenteOutro: String(linha?.concorrenteOutro || "").trim(),
      }))
      .filter((linha) =>
        linha.bancoId
        || linha.convenioId
        || linha.valorProduzido
        || linha.concorrenteId
        || linha.concorrenteOutro
      )
      .map((linha) => {
        const banco = bancosMap.get(linha.bancoId);
        const convenio = conveniosMap.get(linha.convenioId);
        const concorrente = concorrentesMap.get(linha.concorrenteId);

        const valorNumero = parseMoneyBRL(linha.valorProduzido);

        return {
          bancoId: banco?.bancoId || linha.bancoId || null,
          bancoNome: banco?.bancoNome || linha.bancoId || null,
          convenioId: convenio?.convenioId || convenio?.ConvenioId || linha.convenioId || null,
          convenioNome: convenio?.convenioNome || convenio?.ConvenioNome || linha.convenioId || null,
          valorProduzido: Number.isFinite(valorNumero) ? valorNumero : null,
          concorrenteId: linha.concorrenteId && linha.concorrenteId !== "OUTRO" ? linha.concorrenteId : null,
          concorrenteNome:
            linha.concorrenteId === "OUTRO"
              ? "Outro"
              : (concorrente?.nome || (linha.concorrenteId || null)),
          concorrenteOutro: linha.concorrenteId === "OUTRO" ? (linha.concorrenteOutro || null) : null,
        };
      });
  };

  const concluirExecucao = async () => {
    if (!execTask || acaoLoading) return;
    if (!podeConcluir(execTask)) {
      SwalWithTheme.error("Acao nao permitida", "Essa tarefa ja foi concluida/cancelada ou nao pertence ao seu perfil.");
      return;
    }
    const relatorio = (concluirForm.relatorio || "").trim();
    const produto = (concluirForm.produto || "").trim();
    if (produtoObrigatorio && !produto) {
      SwalWithTheme.error("Erro", "Campo Produto negociado e obrigatorio");
      return;
    }

    const dadosFinanceiros = montarDadosFinanceiros();
    const exigeLinhasFinanceiras = bancoObrigatorio || convenioObrigatorio || valorObrigatorio || concorrenteObrigatorio;
    if (exigeLinhasFinanceiras && dadosFinanceiros.length === 0) {
      SwalWithTheme.error("Erro", "Preencha ao menos uma linha de Banco/Convenio/Valor para concluir");
      return;
    }

    const linhaSemBanco = dadosFinanceiros.some((linha) => !linha.bancoId);
    if (bancoObrigatorio && linhaSemBanco) {
      SwalWithTheme.error("Erro", "Campo Banco e obrigatorio em todas as linhas");
      return;
    }

    const linhaSemConvenio = dadosFinanceiros.some((linha) => !linha.convenioId);
    if (convenioObrigatorio && linhaSemConvenio) {
      SwalWithTheme.error("Erro", "Campo Convenio e obrigatorio em todas as linhas");
      return;
    }

    const linhaComValorInvalido = dadosFinanceiros.some((linha) => {
      if (linha.valorProduzido == null) return Boolean(valorObrigatorio);
      const value = Number(linha.valorProduzido);
      return !Number.isFinite(value) || value <= 0;
    });
    if ((valorObrigatorio || exibirValorProduzido) && linhaComValorInvalido) {
      SwalWithTheme.error(
        "Erro",
        valorObrigatorio
          ? "Campo Valor deve ser maior que zero em todas as linhas"
          : "Se informado, o campo Valor deve ser maior que zero."
      );
      return;
    }

    const linhaSemConcorrente = dadosFinanceiros.some((linha) => !String(linha.concorrenteNome || linha.concorrenteId || "").trim());
    if (concorrenteObrigatorio && linhaSemConcorrente) {
      SwalWithTheme.error("Erro", "Campo Concorrente e obrigatorio em todas as linhas");
      return;
    }

    const linhaOutroSemDescricao = dadosFinanceiros.some(
      (linha) => String(linha.concorrenteNome || "").toUpperCase() === "OUTRO" && !String(linha.concorrenteOutro || "").trim()
    );
    if (exibirConcorrente && linhaOutroSemDescricao) {
      SwalWithTheme.error("Erro", "Ao selecionar concorrente 'Outro', informe o nome do concorrente");
      return;
    }

    try {
      setAcaoLoading(true);
      await concluirTarefaComPayload(execTask.id, {
        relatorio,
        dadosFinanceiros: dadosFinanceiros.length ? JSON.stringify(dadosFinanceiros) : null,
        produto: produto || null,
        tipoProduto: null,
        comissaoParceiroAjustada: concluirForm.comissaoParceiroAjustada,
        loginParceiroSolicitado: concluirForm.loginParceiroSolicitado,
      });
      fireTaskCompletedConfetti();
      SwalWithTheme.success("Sucesso", "Tarefa concluida com sucesso");
      clearConcluirDraft(execTask.id);
      setShowExecModal(false);
      setExecTask(null);
      setExecReadOnlyMode(false);
      setChecks([]);
      setExecTaskHistory([]);
      await carregarTarefas();
      await carregarHistoricoParceiro({ page: 0, append: false });
    } catch (error) {
      const message = error?.response?.data?.message || "Erro ao concluir tarefa";
      SwalWithTheme.error("Erro", message);
    } finally {
      setAcaoLoading(false);
    }
  };
  const contatosColumns = [
    { key: "nomeContato", header: "Nome", mobilePrimary: true, mobileOrder: 0 },
    {
      key: "telefones",
      header: "Telefones",
      mobileSpan: 2,
      mobileOrder: 1,
      render: (value) => {
        if (!Array.isArray(value) || value.length === 0) return "N/A";
        return (
          <div className="space-y-1">
            {value.map((tel) => (
              <div key={tel.idTelefone || tel.telefone} className="text-sm">
                {tel.telefone}
                <span className="ml-2 text-xs text-muted-foreground">({tel.tipo || "N/A"})</span>
              </div>
            ))}
          </div>
        );
      },
    },
  ];

  const contatosActions = [
    {
      icon: Plus,
      title: "Adicionar Telefone",
      onClick: handleAdicionarTelefone,
    },
    {
      icon: UserRoundPen,
      title: "Editar Contato",
      onClick: (contato) => {
        setContatoForm({ idContato: contato.idContato, nomeContato: contato.nomeContato });
        setShowContatoModal(true);
      },
    },
    {
      icon: Trash2,
      title: "Remover Contato",
      onClick: (contato) => handleRemoverContato(contato.idContato),
    },
  ];

  const tarefasColumns = [
    {
      key: "idTipoDeTarefa",
      header: "Tipo",
      mobilePrimary: true,
      mobileOrder: 0,
      render: (value, row) => {
        const idTipo = value || row.tipoDeTarefa?.id;
        const tipo = tiposMap.get(String(idTipo));
        const iconDef = resolveTaskTypeIconDefinition(tipo);
        const Icon = iconDef.icon;
        return (
          <div className="flex min-w-0 items-center gap-2.5">
            <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-2xl border border-border bg-muted/30 text-foreground">
              <Icon className="h-4 w-4" />
            </span>
            <span className="truncate font-medium text-foreground">{tipo?.nomeTipo || "N/A"}</span>
          </div>
        );
      },
    },
    {
      key: "dataInicio",
      header: "Data atividade",
      mobileSpan: 2,
      mobileOrder: 1,
      render: (value) => (value ? new Date(value).toLocaleString("pt-BR") : "N/A"),
    },
    {
      key: "motivo",
      header: "Motivo",
      mobileSpan: 2,
      mobileOrder: 2,
      render: (value, row) => {
        const nomesPorId = (row?.motivosIds || [])
          .map((motivoId) => motivosMap.get(String(motivoId))?.nome)
          .filter(Boolean);
        const motivos = nomesPorId.length ? nomesPorId : parseMotivos(value);
        if (!motivos.length) return "N/A";
        const linhaExpandida = motivosExpandidos.has(getLinhaMotivoKey(row));
        const visibles = linhaExpandida ? motivos : motivos.slice(0, MOTIVOS_VISIBLE_BUDGET);
        const extra = motivos.length - visibles.length;
        return (
          <div className="flex flex-wrap gap-1.5" title={motivos.join(", ")}>
            {visibles.map((motivo) => (
              <span
                key={`motivo-parceiro-${row.id}-${motivo}`}
                className="inline-flex items-center rounded-full border border-border bg-muted/50 px-2 py-0.5 text-xs font-medium"
              >
                {motivo}
              </span>
            ))}
            {extra > 0 && !linhaExpandida && (
              <button
                type="button"
                onClick={(event) => {
                  event.stopPropagation();
                  toggleMotivosExpandidos(row);
                }}
                className="inline-flex items-center rounded-full border border-border bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground hover:border-primary/60 hover:text-foreground"
                title={`Ver todos os motivos (${motivos.length})`}
                aria-label={`Ver todos os motivos (${motivos.length})`}
              >
                +{extra}
              </button>
            )}
            {linhaExpandida && motivos.length > MOTIVOS_VISIBLE_BUDGET && (
              <button
                type="button"
                onClick={(event) => {
                  event.stopPropagation();
                  toggleMotivosExpandidos(row);
                }}
                className="inline-flex items-center rounded-full border border-border bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground hover:border-primary/60 hover:text-foreground"
              >
                ver menos
              </button>
            )}
          </div>
        );
      },
    },
    {
      key: "idStatusAtividade",
      header: "Status",
      mobileSpan: 1,
      mobileOrder: 3,
      render: (value, row) => {
        const statusId = value || row.statusAtividade?.id;
        const status = statusOptionMap.get(String(statusId));
        return <StatusBadge status={status?.nome || row?.statusNome || "N/A"} variant={status?.cor || "default"} />;
      },
    },
  ];

  const tarefasActions = [
    {
      icon: Eye,
      title: "Executar / concluir",
      onClick: (row) => abrirExecucao(row),
    },
    {
      icon: UserRoundPen,
      title: "Editar",
      onClick: handleEditarTarefa,
      disabled: (row) => !podeEditar(row),
    },
    {
      icon: Trash2,
      title: "Cancelar",
      onClick: handleExcluirTarefa,
      disabled: (row) => !podeCancelar(row),
    },
  ];

  const statusFilterOptions = statusAtividades.map((s) => ({ value: String(s.id), label: s.nome }));
  const tipoFilterOptions = tiposTarefa.map((t) => ({ value: String(t.id), label: t.nomeTipo }));
  const motivosOptions = motivosList.map((m) => ({ value: String(m.id), label: m.nome }));
  const bancosOptions = bancos
    .map((b) => ({
      value: String(b.bancoId ?? b.BancoId ?? ""),
      label: b.bancoNome ?? b.BancoNome ?? String(b.bancoId ?? b.BancoId ?? ""),
    }))
    .filter((option) => option.value);

  const formatMotivosLabel = (row) => {
    const nomes = (row?.motivosIds || [])
      .map((motivoId) => motivosMap.get(String(motivoId))?.nome)
      .filter(Boolean);
    if (nomes.length) return nomes.join(", ");

    const motivos = parseMotivos(row?.motivo);
    if (!motivos.length) return "N/A";
    const visibles = motivos.slice(0, MOTIVOS_VISIBLE_BUDGET);
    const extra = motivos.length - visibles.length;
    return `${visibles.join(", ")}${extra > 0 ? ` +${extra}` : ""}`;
  };

  const tarefasFiltradas = useMemo(() => {
    return (tarefas || [])
      .filter((tarefa) => {
        if (!searchTerm) return true;
        const term = searchTerm.toLowerCase();
        return [tarefa.descricaoTarefa, tarefa.motivo, tarefa.observacoes, tarefa.relatorio]
          .filter(Boolean)
          .some((value) => String(value).toLowerCase().includes(term));
      })
      .filter((tarefa) => {
        if (filterStatusIds.length === 0) return true;
        return filterStatusIds.includes(String(tarefa.idStatusAtividade || ""));
      })
      .filter((tarefa) => {
        if (filterTipoIds.length === 0) return true;
        return filterTipoIds.includes(String(tarefa.idTipoDeTarefa || ""));
      })
      .filter((tarefa) => {
        if (!filtroDataInicio && !filtroDataFim) return true;
        const dataBase = tarefa.dataInicio || tarefa.dataCriacao;
        if (!dataBase) return false;
        const data = new Date(dataBase);
        if (Number.isNaN(data.getTime())) return false;
        const inicio = filtroDataInicio ? new Date(`${filtroDataInicio}T00:00:00`) : null;
        const fim = filtroDataFim ? new Date(`${filtroDataFim}T23:59:59`) : null;
        if (inicio && data < inicio) return false;
        if (fim && data > fim) return false;
        return true;
      });
  }, [
    tarefas,
    searchTerm,
    filterStatusIds,
    filterTipoIds,
    filtroDataInicio,
    filtroDataFim,
  ]);

  const activeFilterCount =
    filterStatusIds.length +
    filterTipoIds.length +
    (filtroDataInicio && filtroDataInicio !== defaultDateRange.from ? 1 : 0) +
    (filtroDataFim && filtroDataFim !== defaultDateRange.to ? 1 : 0);

  const handleClearTaskFilters = () => {
    setFilterStatusIds([]);
    setFilterTipoIds([]);
    setFiltroDataInicio(defaultDateRange.from);
    setFiltroDataFim(defaultDateRange.to);
    setFiltersOpen(false);
  };

  // Filtros das tarefas do parceiro abrem pelo funil da barra superior (FiltersSheet). O histórico,
  // mais abaixo, mantém busca e período na própria seção: não é tabela e a página só tem um funil.
  usePageFilters({ count: activeFilterCount, onOpen: () => setFiltersOpen(true) });
  // Breadcrumb: Parceiros › Nome do parceiro, no lugar de "Detalhes do parceiro".
  useBreadcrumbEntity(`/parceiros/${id}`, nomeExibicaoParceiro(parceiro?.nome));

  const parceiroAbas = [
    { value: "informacoes", label: "Informações", icon: Building2 },
    { value: "tarefas", label: `Tarefas (${tarefasFiltradas.length})`, icon: ClipboardList },
    { value: "historico", label: "Histórico", icon: History },
    { value: "contatos", label: `Contatos (${contatos.length})`, icon: Users },
  ];

  const producaoBrutaValor = useMemo(
    () =>
      Number(
        joinValoresModo === "total"
          ? joinResumo.producaoBrutaTotal
          : joinResumo.producaoBrutaMedia
      ) || 0,
    [joinValoresModo, joinResumo]
  );

  const producaoLiquidaValor = useMemo(
    () =>
      Number(
        joinValoresModo === "total"
          ? joinResumo.producaoLiquidaTotal
          : joinResumo.producaoLiquidaMedia
      ) || 0,
    [joinValoresModo, joinResumo]
  );

  const producaoRemuneradaValor = useMemo(
    () =>
      Number(
        joinValoresModo === "total"
          ? joinResumo.producaoRemuneradaTotal
          : joinResumo.producaoRemuneradaMedia
      ) || 0,
    [joinValoresModo, joinResumo]
  );

  const producaoBrutaValorAnterior = useMemo(
    () =>
      Number(
        joinValoresModo === "total"
          ? joinResumoAnterior.producaoBrutaTotal
          : joinResumoAnterior.producaoBrutaMedia
      ) || 0,
    [joinValoresModo, joinResumoAnterior]
  );

  const producaoLiquidaValorAnterior = useMemo(
    () =>
      Number(
        joinValoresModo === "total"
          ? joinResumoAnterior.producaoLiquidaTotal
          : joinResumoAnterior.producaoLiquidaMedia
      ) || 0,
    [joinValoresModo, joinResumoAnterior]
  );

  const producaoRemuneradaValorAnterior = useMemo(
    () =>
      Number(
        joinValoresModo === "total"
          ? joinResumoAnterior.producaoRemuneradaTotal
          : joinResumoAnterior.producaoRemuneradaMedia
      ) || 0,
    [joinValoresModo, joinResumoAnterior]
  );

  const cardsVidaAgente = useMemo(() => {
    const sufixoModo = joinValoresModo === "total" ? "total" : "media";
    const items = [
      {
        label: "Contratos",
        value: <NumberFlowValue className="tabular-nums" value={Number(joinResumo.quantidadeContratos || 0)} format={{ maximumFractionDigits: 0 }} />,
        valueLabel: formatCount(joinResumo.quantidadeContratos),
        previousValue: formatCount(joinResumoAnterior.quantidadeContratos),
        icon: Activity,
        change: calculateChange(joinResumo.quantidadeContratos, joinResumoAnterior.quantidadeContratos),
      },
      {
        label: `Producao bruta ${sufixoModo}`,
        value: <NumberFlowValue className="tabular-nums" value={Number(producaoBrutaValor || 0)} format={{ style: "currency", currency: "BRL" }} />,
        valueLabel: formatCurrency(producaoBrutaValor),
        previousValue: formatCurrency(producaoBrutaValorAnterior),
        icon: CalendarDays,
        change: calculateChange(producaoBrutaValor, producaoBrutaValorAnterior),
      },
      {
        label: `Producao liquida ${sufixoModo}`,
        value: <NumberFlowValue className="tabular-nums" value={Number(producaoLiquidaValor || 0)} format={{ style: "currency", currency: "BRL" }} />,
        valueLabel: formatCurrency(producaoLiquidaValor),
        previousValue: formatCurrency(producaoLiquidaValorAnterior),
        icon: Sparkles,
        change: calculateChange(producaoLiquidaValor, producaoLiquidaValorAnterior),
      },
      {
        label: `Producao remunerada ${sufixoModo}`,
        value: <NumberFlowValue className="tabular-nums" value={Number(producaoRemuneradaValor || 0)} format={{ style: "currency", currency: "BRL" }} />,
        valueLabel: formatCurrency(producaoRemuneradaValor),
        previousValue: formatCurrency(producaoRemuneradaValorAnterior),
        icon: CheckCircle2,
        accent: "var(--chart-3)",
        change: calculateChange(producaoRemuneradaValor, producaoRemuneradaValorAnterior),
      },
      {
        label: "Repasse medio",
        value: (
          <NumberFlowValue
            className="tabular-nums"
            value={Number(joinResumo.percentRepasseMedio || 0)}
            format={{ minimumFractionDigits: 2, maximumFractionDigits: 2 }}
            suffix="%"
          />
        ),
        icon: Percent,
        showComparison: false,
      },
      {
        label: "Potencial parceiro",
        value: <NumberFlowValue className="tabular-nums" value={Number(joinResumo.potencialParceiro || 0)} format={{ style: "currency", currency: "BRL" }} />,
        icon: Target,
        accent: "var(--chart-5)",
        showComparison: false,
      },
      {
        label: "Visitas realizadas",
        value: <NumberFlowValue className="tabular-nums" value={Number(visitasRealizadas || 0)} format={{ maximumFractionDigits: 0 }} />,
        icon: LogIn,
        accent: "var(--chart-6)",
        showComparison: false,
      },
      {
        label: "Atividades concluidas",
        value: <NumberFlowValue className="tabular-nums" value={Number(atividadesRealizadas || 0)} format={{ maximumFractionDigits: 0 }} />,
        icon: CheckCircle2,
        accent: "var(--chart-7)",
        showComparison: false,
      },
    ];

    return items.map((item) => ({
      ...item,
      tooltip: buildComparisonTitle(item.valueLabel, item.previousValue),
    }));
  }, [
    joinValoresModo,
    joinResumo,
    joinResumoAnterior,
    producaoBrutaValor,
    producaoLiquidaValor,
    producaoRemuneradaValor,
    producaoBrutaValorAnterior,
    producaoLiquidaValorAnterior,
    producaoRemuneradaValorAnterior,
    visitasRealizadas,
    atividadesRealizadas,
  ]);

  const historicoItems = historicoPage.content || [];

  const contatoProgress = calcProgress([Boolean(String(contatoForm.nomeContato || "").trim())]);
  const telefoneProgress = calcProgress([
    Boolean(String(telefoneForm.numero || "").trim()),
    Boolean(contatoSelecionado?.idContato),
  ]);
  const tarefaProgress = calcProgress([
    Boolean(String(tarefaForm.tipoId || "").trim()),
    Boolean(String(tarefaForm.dataHora || "").trim()),
    Array.isArray(tarefaForm.motivoIds) && tarefaForm.motivoIds.length > 0,
    String(tarefaForm.observacoes || "").trim().length >= 15,
  ]);
  const conclusaoProgress = useMemo(() => {
    if (!execTask) return 0;
    const relatorioOk = String(concluirForm.relatorio || "").trim().length >= 1;
    const produtoOk = !produtoObrigatorio || Boolean(String(concluirForm.produto || "").trim());
    const checkinOkOrPendente = !checkinExigido || checkinOk || checkinPendente;
    const checkoutOkOrPendente = !checkoutExigido || checkoutOk || checkoutPendente;
    const evidenciaOkOrPendente = !exigeEvidencia || evidenciaOk || evidenciaPendente;

    const linhasDigitadas = (concluirForm.bancosConvenios || [])
      .map((linha) => ({
        bancoId: String(linha?.bancoId || "").trim(),
        convenioId: String(linha?.convenioId || "").trim(),
        valorProduzido: String(linha?.valorProduzido || "").trim(),
        concorrenteId: String(linha?.concorrenteId || "").trim(),
        concorrenteOutro: String(linha?.concorrenteOutro || "").trim(),
      }))
      .filter((linha) =>
        linha.bancoId
        || linha.convenioId
        || linha.valorProduzido
        || linha.concorrenteId
        || linha.concorrenteOutro
      );

    let financeiroOk = true;
    if (algumCampoLinhaHabilitado) {
      if ((bancoObrigatorio || convenioObrigatorio || valorObrigatorio || concorrenteObrigatorio) && linhasDigitadas.length === 0) {
        financeiroOk = false;
      } else {
        financeiroOk = linhasDigitadas.every((linha) => {
          if (bancoObrigatorio && !linha.bancoId) return false;
          if (convenioObrigatorio && !linha.convenioId) return false;
          const rawValor = String(linha.valorProduzido || "").trim();
          if (valorObrigatorio || rawValor) {
            const valorNumero = parseMoneyBRL(rawValor);
            if (valorNumero == null || !Number.isFinite(valorNumero) || valorNumero <= 0) return false;
          }
          if (concorrenteObrigatorio && !linha.concorrenteId) return false;
          if (exibirConcorrente && linha.concorrenteId.toUpperCase() === "OUTRO" && !linha.concorrenteOutro) return false;
          return true;
        });
      }
    }

    const fallbackOk = !fallbackLiberado.habilitado || Boolean(String(fallbackForm.motivo || "").trim());

    return calcProgress([
      relatorioOk,
      produtoOk,
      checkinOkOrPendente,
      checkoutOkOrPendente,
      evidenciaOkOrPendente,
      !algumCampoLinhaHabilitado || financeiroOk,
      fallbackOk,
    ]);
  }, [
    execTask,
    concluirForm,
    produtoObrigatorio,
    checkinExigido,
    checkinOk,
    checkinPendente,
    checkoutExigido,
    checkoutOk,
    checkoutPendente,
    exigeEvidencia,
    evidenciaOk,
    evidenciaPendente,
    algumCampoLinhaHabilitado,
    bancoObrigatorio,
    convenioObrigatorio,
    valorObrigatorio,
    exibirConcorrente,
    concorrenteObrigatorio,
    fallbackLiberado.habilitado,
    fallbackForm.motivo,
  ]);
  const execSomenteLeitura = execReadOnlyMode || !podeConcluir(execTask);

  const enderecoMapsQuery = useMemo(() => {
    const cepNormalizado = String(form.cep || "").replace(/\D/g, "");
    const cidadeUf = [form.cidade, form.uf].map((item) => String(item || "").trim()).filter(Boolean).join(" - ");

    return [form.endereco, form.nu_endereco, form.bairro, cidadeUf, cepNormalizado]
      .map((item) => String(item || "").trim())
      .filter(Boolean)
      .join(", ");
  }, [form.endereco, form.nu_endereco, form.bairro, form.cidade, form.uf, form.cep]);

  const googleMapsPlaceUrl = useMemo(() => {
    if (!enderecoMapsQuery) return "";
    return `https://www.google.com/maps/place/${encodeURIComponent(enderecoMapsQuery)}`;
  }, [enderecoMapsQuery]);

  const abrirParceiroNoGoogleMaps = () => {
    if (!googleMapsPlaceUrl) return;
    window.open(googleMapsPlaceUrl, "_blank", "noopener,noreferrer");
  };

  if (loading) {
    return <PageLoader label="Carregando..." />;
  }

  if (!parceiro) {
    return <div className="py-16 text-center text-sm text-muted-foreground">Parceiro nao encontrado</div>;
  }

  return (
    <div className="space-y-5">
      <ParceiroIdentidade parceiro={parceiro} fallbackNome={`Parceiro #${id}`} />

      {/* Vida do agente em largura total, logo abaixo da identidade: é o número que mais se consulta.
          Na lateral de 360px os cartões ficavam empilhados e espremidos. */}
      <Card
        title="Vida do agente"
        actions={[
          <div key="modo-join" className="inline-flex items-center gap-1 rounded-md border border-border bg-background p-1">
            <Button
              type="button"
              size="sm"
              variant={joinValoresModo === "media" ? "secondary" : "ghost"}
              onClick={() => setJoinValoresModo("media")}
            >
              Médias
            </Button>
            <Button
              type="button"
              size="sm"
              variant={joinValoresModo === "total" ? "secondary" : "ghost"}
              onClick={() => setJoinValoresModo("total")}
            >
              Totais
            </Button>
          </div>,
          <Button
            key="vida-detalhada"
            variant="outline"
            size="sm"
            // Leva o período em uso: a detalhada abre no mesmo recorte em vez do que tinha guardado.
            onClick={() => navigate(`/parceiros/${id}/vida-agente?de=${joinPeriodo.dataInicio}&ate=${joinPeriodo.dataFim}`)}
            disabled={!parceiro.idJoin}
          >
            Ver detalhada
            <ArrowUpRight className="h-4 w-4" />
          </Button>,
        ]}
      >
        <p className="text-xs text-muted-foreground">
          {formatPeriodoDescricao(joinPeriodo)}, comparado com {formatPeriodoDescricao(buildJoinPeriodoComparativo(joinPeriodo))}.
          O período muda no filtro da barra superior.
        </p>
        {!parceiro.idJoin && (
          <div className={`mt-3 rounded-lg p-3 text-xs ${toneClassMap.warningPanel}`}>
            Defina o ID Join do parceiro para carregar os cards da vida do agente.
          </div>
        )}

        {/* Cartões em degradê, mesmo padrão da vida do agente detalhada e do dashboard (utils/kpiCard). */}
        <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
          {cardsVidaAgente.map((card) => {
            const Icon = card.icon;
            const { accent, ink } = card.accent ? { accent: card.accent, ink: "light" } : kpiAccentByLabel(card.label);
            const inkClasses = kpiInkClasses(ink);
            const hasComparison = card.showComparison !== false && card.change;
            return (
              <article
                key={card.label}
                className={`relative overflow-hidden rounded-2xl p-4 shadow-md ${inkClasses.root}`}
                style={kpiGradientStyle(accent, ink)}
              >
                <div className="min-w-0 pr-10">
                  <p className={`text-[11px] font-semibold uppercase tracking-[0.16em] ${inkClasses.label}`}>{card.label}</p>
                  <p className="mt-1 break-all font-display text-xl font-semibold leading-tight">{card.value}</p>
                </div>
                <Icon className={`absolute right-4 top-4 h-5 w-5 ${inkClasses.icon}`} aria-hidden="true" />
                {hasComparison ? (
                  <div className="mt-2 flex justify-end">
                    <JoinComparisonPill change={card.change} title={card.tooltip} />
                  </div>
                ) : null}
              </article>
            );
          })}
        </div>
      </Card>

      {/* Informações, tarefas, histórico e contatos em abas, como num CRM: troca de aba em vez de rolar. */}
      <Card>
        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <Tabs
            items={parceiroAbas}
            value={abaAtiva}
            onChange={setAbaAtiva}
            idPrefix="parceiro-abas"
            ariaLabel="Seções do parceiro"
          />
          <div className="flex flex-wrap items-center gap-2">
            {abaAtiva === "informacoes" && !editMode && (
              <>
                {/* Mesmo tamanho (sm) e mesmo formato ícone + texto: antes o do mapa era um quadrado de 40px. */}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={abrirParceiroNoGoogleMaps}
                  disabled={!googleMapsPlaceUrl}
                  title="Abrir no Google Maps"
                >
                  <MapPinned className="h-4 w-4" />
                  Ver no mapa
                </Button>
                <Button variant="outline" size="sm" onClick={() => setEditMode(true)}>
                  <UserRoundPen className="h-4 w-4" />
                  Editar
                </Button>
              </>
            )}
            {abaAtiva === "informacoes" && editMode && (
              <>
                <Button size="sm" onClick={handleSalvarParceiro}>Salvar</Button>
                <Button size="sm" variant="secondary" onClick={handleCancelarEdicaoParceiro}>Cancelar</Button>
              </>
            )}
            {abaAtiva === "tarefas" && (
              <Button size="sm" onClick={handleAdicionarTarefa}>
                <Plus className="h-4 w-4" />
                Nova tarefa
              </Button>
            )}
            {abaAtiva === "contatos" && (
              <Button size="sm" onClick={() => setShowContatoModal(true)}>
                <Plus className="h-4 w-4" />
                Novo contato
              </Button>
            )}
          </div>
        </div>

        {abaAtiva === "informacoes" && (
          <div {...tabPanelProps("parceiro-abas", "informacoes")} className="motion-safe:animate-tab-in">
            {editMode ? (
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <FormField label="Nome" value={form.nome} onChange={(e) => setForm((p) => ({ ...p, nome: e.target.value }))} disabled={!editMode} />
                <FormField
                  label={documentoLabel}
                  value={form.documento}
                  onChange={(e) => handleDocumentoChange(e.target.value)}
                  disabled={!editMode}
                />
                <FormField
                  label="ID Join"
                  type="number"
                  value={form.idJoin}
                  onChange={(e) => setForm((p) => ({ ...p, idJoin: e.target.value }))}
                  disabled={!editMode}
                  required={form.status === "ativo"}
                />
                <FormField
                  label="Status"
                  type="select"
                  value={form.status}
                  onChange={(e) => setForm((p) => ({ ...p, status: e.target.value }))}
                  disabled={!editMode}
                  options={[
                    { value: "ativo", label: "Ativo" },
                    { value: "lead", label: "Lead" },
                    { value: "inativo", label: "Inativo" },
                  ]}
                />
                <FormField
                  label="CEP"
                  value={form.cep}
                  onChange={(e) => setForm((p) => ({ ...p, cep: e.target.value }))}
                  onBlur={handleCepBlur}
                  disabled={!editMode}
                />
                <FormField label="Endereco" value={form.endereco} disabled />
                <FormField
                  label="Numero"
                  value={form.nu_endereco}
                  onChange={(e) => setForm((p) => ({ ...p, nu_endereco: e.target.value }))}
                  disabled={!editMode}
                />
                <FormField label="Bairro" value={form.bairro} disabled />
                <FormField label="Cidade" value={form.cidade} disabled />
                <FormField label="UF" value={form.uf} disabled />
              </div>
            ) : (
              // Leitura como lista rótulo/valor: campo desabilitado tem contraste baixo de propósito
              // e fazia o dado principal parecer secundário. O formulário só aparece ao editar.
              <dl className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2">
                <InfoItem label="Nome">{form.nome}</InfoItem>
                <InfoItem label={documentoLabel}>{form.documento}</InfoItem>
                <InfoItem label="ID Join">{form.idJoin}</InfoItem>
                <InfoItem label="Status">
                  {form.status ? <StatusBadge status={PARCEIRO_STATUS_LABEL[form.status] || form.status} shape="rounded" /> : null}
                </InfoItem>
                <InfoItem label="Endereço" className="sm:col-span-2">{formatEnderecoLinha(form)}</InfoItem>
              </dl>
            )}
          </div>
        )}

        {abaAtiva === "tarefas" && (
          <div {...tabPanelProps("parceiro-abas", "tarefas")} className="motion-safe:animate-tab-in">
            <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
              <div className="w-full lg:max-w-[520px]">
                <SearchBar
                  placeholder="Pesquisar tarefa..."
                  value={searchTerm}
                  onChange={(event) => setSearchTerm(event.target.value)}
                  className="w-full"
                />
              </div>
            </div>

            <DataTable
              columns={tarefasColumns}
              data={tarefasFiltradas}
              actions={tarefasActions}
              enablePagination={false}
            />
            <p className="mt-3 text-xs text-muted-foreground">
              Check-in/check-out usam geolocalizacao do dispositivo e respeitam as regras de distancia do parceiro.
            </p>
          </div>
        )}

        {abaAtiva === "historico" && (
          <div {...tabPanelProps("parceiro-abas", "historico")} className="motion-safe:animate-tab-in">
            <div className="space-y-4">
              <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
                <div className="w-full xl:max-w-[520px]">
                  <SearchBar
                    placeholder="Buscar no historico..."
                    value={historicoTermo}
                    onChange={(event) => setHistoricoTermo(event.target.value)}
                    className="w-full"
                  />
                </div>
                <Button
                  variant="outline"
                  onClick={handleClearHistoryFilters}
                  disabled={
                    !historicoTermo
                    && !historicoDataInicio
                    && !historicoDataFim
                    && historicoCategoriasAtivas.length === HISTORY_CATEGORY_CONFIG.length
                  }
                >
                  Limpar filtros
                </Button>
              </div>

              <DateRangeField
                label="Periodo do historico"
                from={historicoDataInicio}
                to={historicoDataFim}
                onFromChange={(e) => setHistoricoDataInicio(e.target.value)}
                onToChange={(e) => setHistoricoDataFim(e.target.value)}
                showPresets={false}
              />

              <div className="flex flex-wrap gap-2">
                {HISTORY_CATEGORY_CONFIG.map((categoria) => {
                  const Icon = categoria.icon;
                  const active = historicoCategoriasAtivas.includes(categoria.key);
                  return (
                    <button
                      key={categoria.key}
                      type="button"
                      title={active ? `Ocultar ${categoria.label.toLowerCase()}` : `Exibir ${categoria.label.toLowerCase()}`}
                      onClick={() => toggleHistoricoCategoria(categoria.key)}
                      className={`inline-flex h-10 items-center gap-2 rounded-xl border px-3 text-sm font-medium transition ${
                        active
                          ? `${getHistoryToneClasses(categoria.key)} shadow-sm`
                          : "border-border bg-background text-muted-foreground opacity-70"
                      }`}
                    >
                      <Icon className="h-4 w-4" />
                      <span>{categoria.label}</span>
                    </button>
                  );
                })}
              </div>

              <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground">
                <span>
                  Exibindo {Number(historicoItems.length || 0).toLocaleString("pt-BR")} de{" "}
                  {Number(historicoPage.totalElements || 0).toLocaleString("pt-BR")} registros.
                </span>
                {historicoCategoriasAtivas.length === 0 && (
                  <span>Selecione ao menos um icone para voltar a exibir eventos.</span>
                )}
              </div>

              <div className="rounded-xl border border-border bg-background/60">
                <div className="h-[820px] overflow-y-auto p-3 sm:p-4">
                  {historicoLoading ? (
                    <div className="flex h-full items-center justify-center gap-2 text-sm text-muted-foreground">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Carregando historico...
                    </div>
                  ) : historicoCategoriasAtivas.length === 0 ? (
                    <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
                      Todos os filtros por icone foram ocultados.
                    </div>
                  ) : historicoItems.length === 0 ? (
                    <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
                      Nenhum evento encontrado para os filtros selecionados.
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {historicoItems.map((evento) => {
                        const categoria = HISTORY_CATEGORY_CONFIG.find((item) => item.key === evento.categoria);
                        const Icon = categoria?.icon || CalendarDays;
                        const campoLabel = evento?.payload?.campoLabel || evento?.payload?.campo || null;
                        const tarefaRelacionada = Boolean(evento?.tarefaId);

                        return (
                          <article
                            key={evento.id}
                            className={`rounded-2xl border border-border bg-card/80 p-3 shadow-sm ${
                              tarefaRelacionada ? "cursor-pointer transition hover:border-primary/50 hover:bg-card" : ""
                            }`}
                            onClick={tarefaRelacionada ? () => abrirTarefaPeloHistorico(evento) : undefined}
                            onKeyDown={tarefaRelacionada ? (e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                abrirTarefaPeloHistorico(evento);
                              }
                            } : undefined}
                            role={tarefaRelacionada ? "button" : undefined}
                            tabIndex={tarefaRelacionada ? 0 : undefined}
                          >
                            <div className="flex items-start gap-3">
                              <div
                                className={`mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl border ${getHistoryToneClasses(
                                  evento.categoria
                                )}`}
                              >
                                <Icon className="h-4 w-4" />
                              </div>

                              <div className="min-w-0 flex-1">
                                <div className="flex flex-wrap items-center gap-2">
                                  <p className="min-w-0 flex-1 text-sm font-semibold leading-5 text-foreground">
                                    {evento.resumo || evento.acao}
                                  </p>
                                  {campoLabel ? (
                                    <span className="rounded-full border border-border bg-muted/25 px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
                                      {campoLabel}
                                    </span>
                                  ) : null}
                                  {evento.tarefaId ? (
                                    <span className="rounded-full border border-border bg-muted/25 px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
                                      Tarefa #{evento.tarefaId}
                                    </span>
                                  ) : null}
                                </div>

                                <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                                  <Avatar className="h-7 w-7 rounded-xl">
                                    <AvatarImage src={evento.atorFotoPerfilUrl} alt={evento.atorNome || "Webhook"} />
                                    <AvatarFallback>{getAvatarInitials(evento.atorNome || "Webhook")}</AvatarFallback>
                                  </Avatar>
                                  <span className="font-medium">{evento.atorNome || "Webhook"}</span>
                                  <span>&bull;</span>
                                  <span>{formatHistoryDateTime(evento.createdAt)}</span>
                                  <span>&bull;</span>
                                  <span>{String(evento.acao || "").replaceAll("_", " ")}</span>
                                </div>
                              </div>
                            </div>
                          </article>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>

              {!historicoPage.last && historicoCategoriasAtivas.length > 0 && (
                <div className="flex justify-center">
                  <Button
                    variant="outline"
                    onClick={() =>
                      carregarHistoricoParceiro({
                        page: Number(historicoPage.page || 0) + 1,
                        append: true,
                      })
                    }
                    disabled={historicoLoading || historicoLoadingMore}
                  >
                    {historicoLoadingMore && <Loader2 className="h-4 w-4 animate-spin" />}
                    Carregar mais
                  </Button>
                </div>
              )}
            </div>
          </div>
        )}

        {abaAtiva === "contatos" && (
          <div {...tabPanelProps("parceiro-abas", "contatos")} className="motion-safe:animate-tab-in">
            <DataTable columns={contatosColumns} data={contatos} actions={contatosActions} enablePagination={false} />
          </div>
        )}
      </Card>

      {/* Fora das abas: o funil da barra superior abre o painel de qualquer aba. */}
      <FiltersSheet
        open={filtersOpen}
        onOpenChange={setFiltersOpen}
        description="O período vale para as tarefas e para a vida do agente; status e tipo, só para as tarefas."
        onClear={handleClearTaskFilters}
        clearDisabled={activeFilterCount === 0}
      >
              <div className="space-y-3">
                <MultiSelectField
                  label="Status"
                  options={statusFilterOptions}
                  selectedValues={filterStatusIds}
                  onChange={(values) => setFilterStatusIds(values.map(String))}
                  placeholder="Selecione status"
                  searchPlaceholder="Buscar status..."
                  emptyText="Nenhum status disponivel"
                />
                <MultiSelectField
                  label="Tipo"
                  options={tipoFilterOptions}
                  selectedValues={filterTipoIds}
                  onChange={(values) => setFilterTipoIds(values.map(String))}
                  placeholder="Selecione tipos"
                  searchPlaceholder="Buscar tipo..."
                  emptyText="Nenhum tipo disponivel"
                />
                <DateRangeField
                  label="Periodo (entre)"
                  from={filtroDataInicio}
                  to={filtroDataFim}
                  onFromChange={(e) => setFiltroDataInicio(e.target.value)}
                  onToChange={(e) => setFiltroDataFim(e.target.value)}
                />
              </div>
      </FiltersSheet>

      <Modal
        isOpen={showContatoModal}
        onClose={() => setShowContatoModal(false)}
        onCancel={() => {
          setShowContatoModal(false);
          setContatoForm({ idContato: null, nomeContato: "" });
        }}
        onSave={handleSalvarContato}
        title={contatoForm.idContato ? "Editar contato" : "Novo contato"}
        progress={contatoProgress}
        progressLabel="Preenchimento do contato"
      >
        <FormField
          label="Nome"
          value={contatoForm.nomeContato}
          onChange={(e) => setContatoForm((p) => ({ ...p, nomeContato: e.target.value }))}
          required
        />
      </Modal>

      <Modal
        isOpen={showTelefoneModal}
        onClose={() => setShowTelefoneModal(false)}
        onCancel={() => {
          setShowTelefoneModal(false);
          setTelefoneForm({ idTelefone: null, numero: "", tipo: "CELULAR" });
        }}
        onSave={handleSalvarTelefone}
        title={telefoneForm.idTelefone ? "Editar telefone" : "Novo telefone"}
        progress={telefoneProgress}
        progressLabel="Preenchimento do telefone"
      >
        {contatoSelecionado?.telefones?.length > 0 && (
          <div className="mb-3 space-y-2 rounded-lg border border-border bg-muted/25 p-2">
            {contatoSelecionado.telefones.map((tel) => (
              <div key={tel.idTelefone} className="rounded-xl border border-border bg-background px-3 py-2.5">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div className="space-y-1">
                    <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">Telefone</div>
                    <span className="text-sm font-medium text-foreground">{tel.telefone}</span>
                    <div className="text-xs text-muted-foreground">{String(tel.tipo || inferPhoneType(tel.telefone)).toUpperCase()}</div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    className="inline-flex min-h-9 items-center justify-center gap-2 rounded-lg border border-border bg-background px-3 py-2 text-xs font-semibold text-foreground transition hover:border-primary hover:text-primary"
                    onClick={() =>
                      setTelefoneForm({
                        idTelefone: tel.idTelefone,
                        numero: toPhoneInputValue(tel.telefone),
                        tipo: String(tel.tipo || inferPhoneType(tel.telefone)).toUpperCase(),
                      })
                    }
                    title="Editar"
                  >
                    <UserRoundPen className="h-4 w-4" />
                    <span>Editar</span>
                  </button>
                  <button
                    type="button"
                    className="inline-flex min-h-9 items-center justify-center gap-2 rounded-lg border border-destructive/40 bg-background px-3 py-2 text-xs font-semibold text-destructive transition hover:opacity-80"
                    onClick={() => handleRemoverTelefone(tel.idTelefone)}
                    title="Excluir"
                  >
                    <Trash2 className="h-4 w-4" />
                    <span>Excluir</span>
                  </button>
                </div>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="space-y-1.5">
          <label className="text-sm font-medium text-foreground">Numero</label>
          <PhoneInput
            defaultCountry="BR"
            value={telefoneForm.numero || undefined}
            onChange={(value) =>
              setTelefoneForm((prev) => ({
                ...prev,
                numero: value || "",
                tipo: inferPhoneType(value || ""),
              }))
            }
            className="phone-input-field"
          />
        </div>
        <FormField
          label="Tipo"
          type="select"
          value={telefoneForm.tipo}
          onChange={(e) => setTelefoneForm((prev) => ({ ...prev, tipo: e.target.value }))}
          options={[
            { value: "CELULAR", label: "Celular" },
            { value: "FIXO", label: "Fixo" },
          ]}
          required
        />
      </Modal>

            <Modal
        isOpen={showTarefaModal}
        onClose={() => {
          setShowTarefaModal(false);
          setEditingTarefa(null);
          setEditingTarefaId(null);
        }}
        onCancel={() => {
          setShowTarefaModal(false);
          setEditingTarefa(null);
          setEditingTarefaId(null);
        }}
        onSave={handleSalvarTarefaParceiro}
        saveDisabled={savingTarefaModal}
        saveText={savingTarefaModal ? "Salvando..." : editingTarefaId ? "Salvar alteracoes" : "Criar tarefa"}
        title={editingTarefaId ? "Editar tarefa" : "Nova tarefa"}
        maxWidth="max-w-3xl"
        progress={tarefaProgress}
        progressLabel="Preenchimento da tarefa"
      >
        <div className="space-y-3">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <MultiSelectField
              label="Tipo de atividade"
              options={tiposTarefa.map((t) => ({ value: String(t.id), label: t.nomeTipo }))}
              selectedValues={tarefaForm.tipoId ? [String(tarefaForm.tipoId)] : []}
              onChange={(values) =>
                setTarefaForm((prev) => ({
                  ...prev,
                  tipoId: values.length ? String(values[0]) : "",
                }))
              }
              single
              placeholder="Selecione o tipo"
              searchPlaceholder="Buscar tipo..."
            />
             <DatePickerField
               label="Data e hora da atividade"
               type="datetime-local"
               name="dataHora"
               value={tarefaForm.dataHora}
               onChange={(e) => setTarefaForm((prev) => ({ ...prev, dataHora: e.target.value }))}
               min={taskScheduleBounds.min}
               max={taskScheduleBounds.max}
               required
               disabled={Boolean(editingTarefa && !podeEditarDataHora(editingTarefa))}
             />
           </div>

           <div className="rounded-md border border-border bg-muted/25 p-2 text-xs text-muted-foreground">
             A atividade pode ser lancada ate {TASK_SCHEDULE_PAST_WINDOW_DAYS} dias no passado e no maximo para os proximos {TASK_SCHEDULE_WINDOW_DAYS} dias.
           </div>

           <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
             <FormField label="Parceiro selecionado" value={parceiro?.nome || `Parceiro #${id}`} disabled />

            <MultiSelectField
              label="Motivos"
              options={motivosOptions}
              selectedValues={tarefaForm.motivoIds || []}
              onChange={(values) =>
                setTarefaForm((prev) => ({
                  ...prev,
                  motivoIds: values.map(String),
                }))
              }
              placeholder="Selecione motivo(s)"
              searchPlaceholder="Buscar motivo..."
              emptyText="Nenhum motivo disponivel"
            />
          </div>

          <FormField
            label="Observacoes"
            type="textarea"
            value={tarefaForm.observacoes}
            onChange={(e) => setTarefaForm((prev) => ({ ...prev, observacoes: e.target.value }))}
            placeholder="Detalhes iniciais da tarefa"
          />

          <div className="rounded-md border border-border bg-muted/35 p-2 text-xs text-muted-foreground">
            Status nao e informado na criacao: toda tarefa nova entra como Planejada.
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={showExecModal}
        onClose={() => {
          setShowExecModal(false);
          setExecTask(null);
          setExecReadOnlyMode(false);
          setChecks([]);
          setExecTaskHistory([]);
          setGeoCheckDrawer((current) => ({ ...current, open: false }));
        }}
        onCancel={() => {
          setShowExecModal(false);
          setExecTask(null);
          setExecReadOnlyMode(false);
          setChecks([]);
          setExecTaskHistory([]);
          setGeoCheckDrawer((current) => ({ ...current, open: false }));
        }}
        onSave={concluirExecucao}
        title="Execucao e conclusao da tarefa"
        saveText={acaoLoading ? "Processando..." : !podeConcluir(execTask) ? "Tarefa concluida" : "Concluir tarefa"}
        saveDisabled={acaoLoading || execSomenteLeitura}
        showActions={!execReadOnlyMode}
        maxWidth="max-w-6xl"
        maxBodyHeight="max-h-[82vh]"
        progress={conclusaoProgress}
        progressLabel="Progresso da conclusao"
      >
        {!execTask ? (
          <div className="py-8 text-center text-sm text-muted-foreground">Selecione uma tarefa.</div>
        ) : (
          <fieldset disabled={execSomenteLeitura} className="space-y-4">
            <div className="rounded-lg border border-border bg-muted/20 p-3 text-sm">
              <p><strong>Tipo:</strong> {tiposTarefa.find((t) => String(t.id) === String(execTask.idTipoDeTarefa))?.nomeTipo || "N/A"}</p>
              <p><strong>Motivos:</strong> {formatMotivosLabel(execTask)}</p>
              <p><strong>Data:</strong> {execTask.dataInicio ? new Date(execTask.dataInicio).toLocaleString("pt-BR") : "N/A"}</p>
              <p><strong>Observacoes:</strong> {execTask.observacoes || execTask.descricaoTarefa || "N/A"}</p>
            </div>

            {execReadOnlyMode && (
              <div className="rounded-md border border-border bg-muted/35 p-2 text-xs text-muted-foreground">
                Essa tarefa foi aberta a partir do historico do parceiro e esta em modo de visualizacao.
              </div>
            )}

            {!podeConcluir(execTask) && (
              <div className="rounded-md border border-border bg-muted/35 p-2 text-xs text-muted-foreground">
                Essa tarefa ja foi concluida/cancelada ou nao pode ser concluida no seu perfil. Os dados estao em modo de visualizacao.
              </div>
            )}

            <div className="flex flex-wrap gap-2 text-xs text-muted-foreground">
              <span className="rounded-full border border-border bg-muted/20 px-2 py-1">
                {checkinExigido ? "Check-in habilitado" : "Sem check-in"}
              </span>
              <span className="rounded-full border border-border bg-muted/20 px-2 py-1">
                {checkoutExigido ? "Check-out habilitado" : "Sem check-out"}
              </span>
              <span className="rounded-full border border-border bg-muted/20 px-2 py-1">
                {permiteFallback ? (fallbackExigeAprovacao ? "Fallback com aprovacao" : "Fallback livre") : "Sem fallback"}
              </span>
              <span className="rounded-full border border-border bg-muted/20 px-2 py-1">
                {exigeEvidencia ? (evidenciaExigeAprovacao ? "Evidencia com aprovacao" : "Evidencia obrigatoria") : "Sem evidencia"}
              </span>
            </div>

            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <Button
                variant="outline"
                className={
                  checkinOk
                    ? toneClassMap.successOutlineButtonActive
                    : toneClassMap.successOutlineButton
                }
                onClick={() => setGeoCheckDrawer({ open: true, action: "checkin" })}
                disabled={execLoading || acaoLoading || !checkinExigido || checkinOk || !podeConcluir(execTask)}
              >
                <LogIn className="h-4 w-4" />
                {checkinOk ? "Check-in realizado" : checkinExigido ? "Realizar check-in" : "Check-in nao exigido"}
              </Button>
              <Button
                variant="outline"
                className={
                  checkoutOk
                    ? toneClassMap.dangerOutlineButtonActive
                    : toneClassMap.dangerOutlineButton
                }
                onClick={() => setGeoCheckDrawer({ open: true, action: "checkout" })}
                disabled={execLoading || acaoLoading || !checkoutExigido || checkoutOk || !podeConcluir(execTask)}
              >
                <LogOut className="h-4 w-4" />
                {checkoutOk ? "Check-out realizado" : checkoutExigido ? "Realizar check-out" : "Check-out nao exigido"}
              </Button>
            </div>

            {(checkinPendente || checkoutPendente || evidenciaPendente) && (
              <div className={`rounded-md p-2 text-xs ${toneClassMap.warningPanel}`}>
                Existe check pendente de aprovacao. Enquanto houver pendencia, a conclusao da tarefa fica bloqueada.
              </div>
            )}

            {exigeEvidencia && (
              <section className="space-y-3 rounded-lg border border-border p-3">
                <h3 className="text-sm font-semibold">Evidencia de conclusao</h3>
                {checkEvidencia ? (
                  <div className="rounded-xl border border-border bg-muted/20 p-3.5 space-y-3.5">
                    <div>
                      <span className="text-[11px] font-semibold text-muted-foreground block uppercase tracking-wider">Observação Registrada</span>
                      <p className="text-sm text-foreground mt-1 whitespace-pre-wrap">{checkEvidencia.motivoFallback || "Nenhuma observação informada."}</p>
                    </div>
                    {checkEvidencia.fotoUrls && checkEvidencia.fotoUrls.length > 0 && (
                      <div>
                        <span className="text-[11px] font-semibold text-muted-foreground block uppercase tracking-wider mb-2">Imagens Enviadas</span>
                        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                          {checkEvidencia.fotoUrls.map((url) => {
                            const absoluteUrl = `${httpClient.defaults.baseURL}${url}`;
                            return (
                              <a 
                                key={url} 
                                href={absoluteUrl} 
                                target="_blank" 
                                rel="noreferrer" 
                                className="overflow-hidden rounded-xl border border-border/70 group relative block aspect-video bg-muted/10 hover:border-primary/50 transition-colors shadow-sm"
                              >
                                <img 
                                  src={absoluteUrl} 
                                  alt="Evidência" 
                                  className="h-full w-full object-cover group-hover:scale-[1.03] transition-transform duration-250 ease-out" 
                                />
                              </a>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <>
                    <FormField
                      label="Observacao da evidencia"
                      value={evidenciaForm.observacao}
                      onChange={(e) => setEvidenciaForm((prev) => ({ ...prev, observacao: e.target.value }))}
                      placeholder="Detalhes da evidencia enviada"
                    />
                    <div className="space-y-1.5">
                      <label className="text-sm font-medium text-foreground">
                        Imagens
                        {evidenciaExigeImagem ? <span className="ml-1 text-destructive">*</span> : null}
                      </label>
                      <input
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={(e) =>
                          setEvidenciaForm((prev) => ({
                            ...prev,
                            files: Array.from(e.target.files || []),
                          }))
                        }
                        className="flex h-10 w-full rounded-lg border border-input bg-background px-3 py-2 text-sm"
                      />
                      {evidenciaForm.files.length > 0 && (
                        <p className="text-xs text-muted-foreground">{evidenciaForm.files.length} arquivo(s) selecionado(s).</p>
                      )}
                    </div>

                    {evidenciaPreviews.length > 0 && (
                      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                        {evidenciaPreviews.map((preview) => (
                          <div key={`evidencia-${preview.url}`} className="overflow-hidden rounded-md border border-border/70">
                            <img src={preview.url} alt={preview.name} className="h-20 w-full object-cover" />
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}

                <Button
                  type="button"
                  variant="outline"
                  disabled={acaoLoading || evidenciaOk || evidenciaPendente || !podeConcluir(execTask)}
                  onClick={enviarEvidencia}
                >
                  {acaoLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                  {evidenciaPendente 
                    ? "Evidencia pendente de aprovação" 
                    : evidenciaOk 
                      ? "Evidencia registrada" 
                      : "Enviar evidencia"}
                </Button>
                <div className="rounded-md border border-border bg-muted/35 p-2 text-xs text-muted-foreground">
                  <CheckCircle2 className="mr-1 inline h-3.5 w-3.5" />
                  A evidencia aparece porque o tipo exige evidencia. {evidenciaExigeAprovacao ? "Ela precisa de aprovacao do admin." : "Ela nao precisa de aprovacao."}
                </div>
              </section>
            )}

            {algumCampoFinanceiroHabilitado && (
              <section className="space-y-3 rounded-lg border border-border p-3">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-semibold">Campos da conclusao</h3>
                  {algumCampoLinhaHabilitado ? (
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() =>
                        setConcluirForm((prev) => ({
                          ...prev,
                          bancosConvenios: [...prev.bancosConvenios, createBancoLinha()],
                        }))
                      }
                    >
                      <Plus className="h-3.5 w-3.5" />
                      Adicionar linha
                    </Button>
                  ) : null}
                </div>

                {exibirProdutoNegociado ? (
                  <FormField
                    label="Produto negociado"
                    required={produtoObrigatorio}
                    value={concluirForm.produto || ""}
                    onChange={(e) => setConcluirForm((prev) => ({ ...prev, produto: e.target.value }))}
                    placeholder="Descreva o produto negociado"
                  />
                ) : null}

                {algumCampoLinhaHabilitado ? concluirForm.bancosConvenios.map((linha, idx) => {
                  const totalCamposVisiveis = [
                    exibirBancoNegociado,
                    exibirConvenioNegociado,
                    exibirValorProduzido,
                    exibirConcorrente,
                  ].filter(Boolean).length;

                  return (
                    <div
                      key={`linha-banco-${idx}`}
                      className="grid grid-cols-1 gap-3 rounded-md border border-border/70 p-3 md:grid-cols-[repeat(4,minmax(0,1fr))_auto]"
                    >
                      {exibirBancoNegociado ? (
                        <MultiSelectField
                          label="Banco"
                          required={bancoObrigatorio}
                          options={bancosOptions}
                          selectedValues={linha.bancoId ? [String(linha.bancoId)] : []}
                          onChange={(values) => {
                            const nextBancoId = values?.[0] ? String(values[0]) : "";
                            setConcluirForm((prev) => ({
                              ...prev,
                              bancosConvenios: prev.bancosConvenios.map((item, itemIdx) =>
                                itemIdx === idx
                                  ? { ...item, bancoId: nextBancoId }
                                  : item
                              ),
                            }));
                          }}
                          single
                          placeholder="Selecione banco"
                          searchPlaceholder="Buscar banco..."
                        />
                      ) : (
                        <div />
                      )}

                      {exibirConvenioNegociado ? (
                        <MultiSelectField
                          label="Convenio"
                          required={convenioObrigatorio}
                          options={conveniosOptions}
                          selectedValues={linha.convenioId ? [String(linha.convenioId)] : []}
                          onChange={(values) =>
                            setConcluirForm((prev) => ({
                              ...prev,
                              bancosConvenios: prev.bancosConvenios.map((item, itemIdx) =>
                                itemIdx === idx ? { ...item, convenioId: values?.[0] ? String(values[0]) : "" } : item
                              ),
                            }))
                          }
                          single
                          placeholder="Selecione convenio"
                          searchPlaceholder="Buscar convenio..."
                        />
                      ) : (
                        <div />
                      )}

                      {exibirValorProduzido ? (
                        <FormField
                          label="Valor produzido"
                          required={valorObrigatorio}
                          type="text"
                          inputMode="decimal"
                          value={linha.valorProduzido || ""}
                          onChange={(e) =>
                            setConcluirForm((prev) => ({
                              ...prev,
                              bancosConvenios: prev.bancosConvenios.map((item, itemIdx) =>
                                itemIdx === idx ? { ...item, valorProduzido: e.target.value } : item
                              ),
                            }))
                          }
                          onBlur={() =>
                            setConcluirForm((prev) => ({
                              ...prev,
                              bancosConvenios: prev.bancosConvenios.map((item, itemIdx) =>
                                itemIdx === idx
                                  ? { ...item, valorProduzido: formatMoneyBRL(item.valorProduzido) || "" }
                                  : item
                              ),
                            }))
                          }
                          placeholder="Ex.: R$ 12.000,50"
                        />
                      ) : (
                        <div />
                      )}

                      {exibirConcorrente ? (
                        <div className="space-y-2">
                          <MultiSelectField
                            label="Concorrente"
                            required={concorrenteObrigatorio}
                            options={concorrentesOptions}
                            selectedValues={linha.concorrenteId ? [String(linha.concorrenteId)] : []}
                            onChange={(values) => {
                              const nextConcorrenteId = values?.[0] ? String(values[0]) : "";
                              setConcluirForm((prev) => ({
                                ...prev,
                                bancosConvenios: prev.bancosConvenios.map((item, itemIdx) =>
                                  itemIdx === idx
                                    ? {
                                        ...item,
                                        concorrenteId: nextConcorrenteId,
                                        concorrenteOutro: nextConcorrenteId === "OUTRO" ? item.concorrenteOutro : "",
                                      }
                                    : item
                                ),
                              }));
                            }}
                            single
                            placeholder="Selecione concorrente"
                            searchPlaceholder="Buscar concorrente..."
                          />

                          {String(linha.concorrenteId || "").toUpperCase() === "OUTRO" && (
                            <FormField
                              label="Qual concorrente?"
                              value={linha.concorrenteOutro || ""}
                              onChange={(e) =>
                                setConcluirForm((prev) => ({
                                  ...prev,
                                  bancosConvenios: prev.bancosConvenios.map((item, itemIdx) =>
                                    itemIdx === idx ? { ...item, concorrenteOutro: e.target.value } : item
                                  ),
                                }))
                              }
                              placeholder="Digite o nome do concorrente"
                            />
                          )}
                        </div>
                      ) : (
                        <div />
                      )}

                      <div className="flex items-end justify-end">
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() =>
                            setConcluirForm((prev) => ({
                              ...prev,
                              bancosConvenios:
                                prev.bancosConvenios.length > 1
                                  ? prev.bancosConvenios.filter((_, i) => i !== idx)
                                  : [createBancoLinha()],
                            }))
                          }
                        >
                          Remover
                        </Button>
                      </div>

                      {totalCamposVisiveis === 0 && (
                        <div className="col-span-full text-xs text-muted-foreground">
                          Nenhum campo financeiro habilitado para os motivos desta tarefa.
                        </div>
                      )}
                    </div>
                  );
                }) : (
                  <div className="rounded-md border border-dashed border-border/70 p-3 text-xs text-muted-foreground">
                    Nenhum campo em linha foi habilitado para os motivos desta tarefa.
                  </div>
                )}
              </section>
            )}

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="space-y-2">
                <p className="text-sm font-medium text-foreground">Foi ajustado comissao do parceiro?</p>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant={concluirForm.comissaoParceiroAjustada === true ? "secondary" : "outline"}
                    onClick={() => setConcluirForm((prev) => ({ ...prev, comissaoParceiroAjustada: true }))}
                    disabled={acaoLoading}
                  >
                    <Check className="h-4 w-4" />
                    Sim
                  </Button>
                  <Button
                    type="button"
                    variant={concluirForm.comissaoParceiroAjustada === false ? "secondary" : "outline"}
                    onClick={() => setConcluirForm((prev) => ({ ...prev, comissaoParceiroAjustada: false }))}
                    disabled={acaoLoading}
                  >
                    <XCircle className="h-4 w-4" />
                    Nao
                  </Button>
                  <Button
                    type="button"
                    variant={concluirForm.comissaoParceiroAjustada == null ? "secondary" : "outline"}
                    onClick={() => setConcluirForm((prev) => ({ ...prev, comissaoParceiroAjustada: null }))}
                    disabled={acaoLoading}
                  >
                    Nao informado
                  </Button>
                </div>
              </div>
              <div className="space-y-2">
                <p className="text-sm font-medium text-foreground">Foi solicitado login do parceiro?</p>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant={concluirForm.loginParceiroSolicitado === true ? "secondary" : "outline"}
                    onClick={() => setConcluirForm((prev) => ({ ...prev, loginParceiroSolicitado: true }))}
                    disabled={acaoLoading}
                  >
                    <Check className="h-4 w-4" />
                    Sim
                  </Button>
                  <Button
                    type="button"
                    variant={concluirForm.loginParceiroSolicitado === false ? "secondary" : "outline"}
                    onClick={() => setConcluirForm((prev) => ({ ...prev, loginParceiroSolicitado: false }))}
                    disabled={acaoLoading}
                  >
                    <XCircle className="h-4 w-4" />
                    Nao
                  </Button>
                  <Button
                    type="button"
                    variant={concluirForm.loginParceiroSolicitado == null ? "secondary" : "outline"}
                    onClick={() => setConcluirForm((prev) => ({ ...prev, loginParceiroSolicitado: null }))}
                    disabled={acaoLoading}
                  >
                    Nao informado
                  </Button>
                </div>
              </div>
            </div>
            <FormField
              label="Detalhes da conclusao"
              type="textarea"
              value={concluirForm.relatorio}
              onChange={(e) => setConcluirForm((prev) => ({ ...prev, relatorio: e.target.value }))}
              placeholder="Descreva execucao, resultados e pendencias."
              inputClassName="min-h-[180px] sm:min-h-[220px]"
            />
            <div className="text-xs text-muted-foreground">Caracteres: {(concluirForm.relatorio || "").trim().length}</div>

            <div className="rounded-md border border-border bg-muted/35 p-2 text-xs text-muted-foreground">
              <CheckCircle2 className="mr-1 inline h-3.5 w-3.5" />
              A conclusao valida regras do tipo de tarefa e campos obrigatorios por motivo.
            </div>

            <section className="space-y-3 rounded-lg border border-border p-3">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-foreground">Timeline da tarefa</h3>
                <span className="text-xs text-muted-foreground">
                  {Number(execTaskHistory.length || 0).toLocaleString("pt-BR")} evento(s)
                </span>
              </div>

              <div className="max-h-[26rem] space-y-3 overflow-y-auto pr-1">
                {execTaskHistoryLoading ? (
                  <div className="flex items-center justify-center gap-2 rounded-lg border border-dashed border-border/70 p-4 text-sm text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Carregando timeline da tarefa...
                  </div>
                ) : execTaskHistory.length === 0 ? (
                  <div className="rounded-lg border border-dashed border-border/70 p-4 text-sm text-muted-foreground">
                    Nenhum evento registrado para esta tarefa.
                  </div>
                ) : (
                  execTaskHistory.map((evento) => {
                    const categoria = HISTORY_CATEGORY_CONFIG.find((item) => item.key === evento.categoria);
                    const Icon = categoria?.icon || CalendarDays;
                    const campoLabel = evento?.payload?.campoLabel || evento?.payload?.campo || null;

                    return (
                      <article
                        key={`exec-historico-${evento.id}`}
                        className="rounded-2xl border border-border bg-card/80 p-3 shadow-sm"
                      >
                        <div className="flex items-start gap-3">
                          <div
                            className={`mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl border ${getHistoryToneClasses(
                              evento.categoria
                            )}`}
                          >
                            <Icon className="h-4 w-4" />
                          </div>

                          <div className="min-w-0 flex-1">
                            <div className="flex flex-wrap items-center gap-2">
                              <p className="min-w-0 flex-1 text-sm font-semibold leading-5 text-foreground">
                                {evento.resumo || evento.acao}
                              </p>
                              {campoLabel ? (
                                <span className="rounded-full border border-border bg-muted/25 px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
                                  {campoLabel}
                                </span>
                              ) : null}
                            </div>

                            <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                              <Avatar className="h-7 w-7 rounded-xl">
                                <AvatarImage src={evento.atorFotoPerfilUrl} alt={evento.atorNome || "Webhook"} />
                                <AvatarFallback>{getAvatarInitials(evento.atorNome || "Webhook")}</AvatarFallback>
                              </Avatar>
                              <span className="font-medium">{evento.atorNome || "Webhook"}</span>
                              <span>&bull;</span>
                              <span>{formatHistoryDateTime(evento.createdAt)}</span>
                              <span>&bull;</span>
                              <span>{String(evento.acao || "").replaceAll("_", " ")}</span>
                            </div>
                          </div>
                        </div>
                      </article>
                    );
                  })
                )}
              </div>
            </section>
          </fieldset>
        )}
      </Modal>
      <TaskGeoCheckDrawer
        open={showExecModal && geoCheckDrawer.open}
        onOpenChange={(nextOpen) => setGeoCheckDrawer((current) => ({ ...current, open: nextOpen }))}
        action={geoCheckDrawer.action}
        task={execTask}
        partner={execTaskGeoPartner}
        canSubmit={!execSomenteLeitura}
        busy={acaoLoading}
        fallbackEnabled={permiteFallback}
        fallbackRequiresImage={fallbackExigeImagem}
        fallbackRequiresApproval={fallbackExigeAprovacao}
        fallbackState={fallbackLiberado}
        fallbackForm={fallbackForm}
        fallbackPreviews={fallbackPreviews}
        onFallbackMotivoChange={(motivo) => setFallbackForm((prev) => ({ ...prev, motivo }))}
        onFallbackFilesChange={(files) => setFallbackForm((prev) => ({ ...prev, files }))}
        onConfirm={handleGeoCheckConfirm}
        onSubmitFallback={handleSubmitFallback}
      />
    </div>
  );
}



